import store from "@/store";
import {financialNationalityMap} from "@/enums/financials/financialNationality";
import {rgpdLabelsByFinancial} from "@/enums/financials/rgpd";
import { financialNameById } from "@/enums/financials/financialNameById";
import {companyPersonTabMapping, particularPersonTabMapping} from "@/enums/proposal/proposalFormType";
import i18n from "@/i18n";
import {FINANCIAL_IDS} from "@/enums/financials/financialIds";


/**
 * Financeiras que usam categoria profissional com jobs
 */
export const FINANCIALS_WITH_JOB_CATEGORY = [
    FINANCIAL_IDS.SOFINCO,
    FINANCIAL_IDS.CETELEM_ES,
    FINANCIAL_IDS.COFIDIS_PT
];

/**
 * Financeiras espanholas que requerem chassis obrigatório
 */
export const FINANCIALS_REQUIRING_CHASSIS = [
    FINANCIAL_IDS.BBVA_ESPANHA,
    FINANCIAL_IDS.CAIXABANK,
    FINANCIAL_IDS.CETELEM_ES,
    FINANCIAL_IDS.SABADELL
];

/**
 * Verifica se a financeira usa categoria profissional com jobs
 * @param {number} financialId - ID da financeira
 * @returns {boolean}
 */
export function hasJobCategory(financialId) {
    return FINANCIALS_WITH_JOB_CATEGORY.includes(Number(financialId));
}

/**
 * Verifica se a financeira requer chassis obrigatório
 * @param {number} financialId - ID da financeira
 * @returns {boolean}
 */
export function requiresChassis(financialId) {
    return FINANCIALS_REQUIRING_CHASSIS.includes(Number(financialId));
}

const nibHolderParticipantMap = {
    1: "first",
    2: "second",
    3: "third",
    4: "fourth"
}

// Função para mapear serviço para dentro do formulário
function getValueFormData(data, formKey, tabKey, fieldKey) {
    let value = data ? data[formKey][tabKey][fieldKey] : "";
    if (fieldKey.startsWith("gender")) value = value === "M" ? 1 : value === "F" ? 2 : 1;
    if (fieldKey.startsWith("address1SameAddress2")) value = value === true ? 1 : value === false ? 2 : 1;
    // ! Set do valor temporário
    if (fieldKey.startsWith("address1_country")) value = "Portugal";
    if (fieldKey.startsWith("address2_country")) value = "Portugal";

    return value;
}

// eslint-disable-next-line
function concatKeyValues(obj, keySuffix) {
    for (let prop in obj) {
        if (typeof obj[prop] === 'object' && obj[prop] !== null) {
            concatKeyValues(obj[prop], keySuffix);
        } else if (prop === 'key') {
            obj[prop] = obj[prop] + "_" + keySuffix;
        }
    }
    return obj;
}

function setFieldsVisibilityAndRequirements(fields, show, country = 'PT') {
    Object.values(fields).forEach(field => {
        //! Dummy way - remover no array de Nacionalidades do campo input PT para certificar que o fieldShow está false
        if (!show) field.proposalType.splice(field.proposalType.indexOf(country), 1);
        field.fieldShow = show;
        field.required = show;
    });
}

function sofincoProfessionalCategoryValidation(form, tab, field) {
    const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
    const nrSalaryPayments = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nrSalaryPayments"));
    const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));

    // Todas as opções para bloquear
    const excludedOccupations = ["Estudiante", "Ama de casa", "Desempleado sin prestación"];
    // As restantes opções - marteladas - != do formModule porque ainda não temos os "options" carregados neste "field"
    const includedOccupations = ["Cuenta ajena", "Empleado público"];
    professionalStatus.required = field.value !== "Estudiante";

    // Fields required consoante a profissão
    const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
    // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
    const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalCategory?.value);
            }
            field.editable = includedOccupations.includes(professionalCategory?.value);
            field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        return field;
    });

    nrSalaryPayments.required = !excludedOccupations.includes(field.value);
    netReceiptValueAvg.required = !excludedOccupations.includes(field.value);

    // https://dtogsc.atlassian.net/browse/SIFA-1413
    // Ama de casa - nºpagas 0
    // Se a categoria profissional for ama de casa, então o número de pagas deve ser zero.
    // ! Risco em ter o timeout - Procurar alternativas melhores
    if (field.value === "Ama de casa") {
        nrSalaryPayments.value = 0;
        nrSalaryPayments.editable = false;
        nrSalaryPayments.keyReRender += 1;
    }
}

function bbvaESProfessionalCategoryValidation(form, tab, field) {
    const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
    const nrSalaryPayments = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nrSalaryPayments"));
    const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));

    // Todas as opções para bloquear
    const excludedOccupations = ["Parado", "Desempleado", "Pensionista/Jubilado", "Otros", "Rentista", "Estudiante", "Renta viudedad", "Ama de casa"];
    // As restantes opções - marteladas - != do formModule porque ainda não temos os "options" carregados neste "field"
    const includedOccupations = ["Fijo", "Temporal", "Asalariado Temporero", "Funcionario", "En practicas", "Empleado del grupo BBVA", "Fijo discontinuo", "Autónomo"];
    professionalStatus.required = field.value !== "Estudiante";

    // Fields required consoante a profissão
    const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
    // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
    const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalCategory?.value);
            }
            field.editable = includedOccupations.includes(professionalCategory?.value);
            field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        return field;
    });

    nrSalaryPayments.required = !excludedOccupations.includes(field.value);
    netReceiptValueAvg.required = !excludedOccupations.includes(field.value);
}

function caixabankProfessionalCategoryValidation(form, tab, field) {
  // const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
  const nrSalaryPayments = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));
  const dtHiredEnd = tab.form.find((form) => form?.key?.startsWith("professionalStatus")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("DTHiredEndText"));
  const companyActivity = tab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
  const job = tab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("job"));
  const dtHired = tab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("DTHiredText"));

  // Todas as opções para bloquear
  const excludedOccupations = ["Pensionista (jubilación)", "Pensionista (viudedad)", "Pensionista (larga enfermedad)", "Pensionista (orfandad)"];
  const semiExcludedOccupations = ["Desempleado", "Otros", "Pensionista (enfermedad temp.)"]; //"Fijo Discontinuo"

  const includedOccupations = ["Asalariado fijo", "Asalariado temporal", "Funcionario", "Autónomo", "Becario/Practicas", "Fijo Discontinuo"];

  // Fields required consoante a profissão
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
    "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  // Inclusão de todas as variantes dos participantes
  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

  tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(professionalCategory?.value);
      }
      field.editable = includedOccupations.includes(professionalCategory?.value);
      field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
    }
    return field;
  });

  dtHiredEnd.required = field.text === "Asalariado temporal";

  if (semiExcludedOccupations.includes(field.value)) {
    companyActivity.required = true;
    companyActivity.editable = true;
    companyActivity.hasError = false;
    companyActivity.value = "";
    companyActivity.options?.forEach((option) => {
      option.selected = option?.value === -1;
    });

    job.required = true;
    job.editable = true;
    job.hasError = false;
    job.value = "";
    job.options?.forEach((option) => {
      option.selected = option?.value === -1;
    });
  }

  if (field.value === "Fijo Discontinuo") {
    dtHired.required = true;
    dtHired.editable = true;
    dtHired.hasError = false;
  }

  nrSalaryPayments.required = !excludedOccupations.includes(field.value);
  netReceiptValueAvg.required = !excludedOccupations.includes(field.value);
  
}

function cetelemSpainProfessionalCategoryValidation(form, tab, field) {
  // const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
  const nrSalaryPayments = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nrSalaryPayments"));
  const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));
  const dtHiredEnd = tab.form.find((form) => form?.key?.startsWith("professionalStatus")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("DTHiredEndText"));
  const dtHired = tab.form.find((form) => form?.key?.startsWith("professionalStatus")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("DTHiredText"));

  // Todas as opções para bloquear
  const excludedOccupations = ["Estudiante", "Jubilado", "No empleado. Demanda empleo", "Otros pasivos"];

  const includedOccupations = ["Trabajador por cuenta propia", "Trabajador por cuenta ajena", "Rentista", "Funcionario"];

  // Fields required consoante a profissão ["companyPostalCode1", "companyCity",]
  const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
     "companyPostalCode2", "companyPostalCode1", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

  const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
     "companyPostalCode2", "companyNIF", "companyCAE", "principalActivityCountry"];

  // Inclusão de todas as variantes dos participantes
  const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
  const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);
  tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
    const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
    if (allRequiredFieldKeys.includes(field.key)) {
      if (allOnlyRequiredFieldKeys.includes(field.key)) {
        field.required = includedOccupations.includes(professionalCategory?.value);
      }
      if (field.key?.startsWith("DTHiredEndText")) {
        field.fieldShow = includedOccupations.includes(professionalCategory?.value);
      }
      field.editable = includedOccupations.includes(professionalCategory?.value);
      field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
    }
    return field;
  });

  if (field.key?.startsWith("DTHiredEndText")) {
    dtHiredEnd.required = includedOccupations.includes(field.text);
  }

  dtHired.required = true;
  dtHired.editable = true;
  nrSalaryPayments.required = !excludedOccupations.includes(field.value);
  netReceiptValueAvg.required = !excludedOccupations.includes(field.value);
  
}

function sabadellProfessionalCategoryValidation(form, tab, field) {
    const nrSalaryPayments = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nrSalaryPayments"));
    const companyActivity = tab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalStatus"));
    const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));
    const dtHiredEnd = tab.form.find((form) => form?.key?.startsWith("professionalStatus")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("DTHiredEndText"));

    // Todas as opções para bloquear
    const excludedOccupations = ["Parado", "Jubilado", "Pensionista", "No Trabaja"];

    const includedOccupations = ["Asalariado Fijo", "Asalariado Temporal", "Funcionario", "Autonomo", "Becario/Practicas", "Temporero"];

    // Fields required consoante a profissão
    const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    const exceptionRequiredFieldsKeys = ["job", "companyActivity"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);
    const allExceptionRequiredFieldsKeys = addVariations(exceptionRequiredFieldsKeys);
    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalCategory?.value);
            }
            field.editable = includedOccupations.includes(professionalCategory?.value);
            field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        if (allExceptionRequiredFieldsKeys.includes(field.key)) {
            if (allExceptionRequiredFieldsKeys.includes(field.key)) {
                field.required = excludedOccupations.includes(professionalCategory?.value);
            }
            field.editable = false;
            field.value = excludedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        return field;
    });

    companyActivity.fieldShow = false;
    dtHiredEnd.required = field.text === "Asalariado Temporal" || field.text === "Temporero";

    nrSalaryPayments.required = !excludedOccupations.includes(field.value);
    netReceiptValueAvg.required = !excludedOccupations.includes(field.value);
}

function sabadellCompanySectorValidation(form, tab) {
    const companyActivity = tab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("companyActivity"));
    const job = tab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("job"));

    switch (job.value) {
        case "Diplomático":
            companyActivity.value = "Diplomatica";
            companyActivity.editable = false;
            break;
        case "Religioso":
            companyActivity.value = "Otras Act. Asociativas";
            companyActivity.editable = false;
            break
        case "Artista":
        case "Deportista Profesional":
        case "Escritor":
            companyActivity.value = "Act. Recre. Y Entretenimi";
            companyActivity.editable = false;
            break;
        default:
            break;
    }
}

function bbvaESCompanySectorValidation(form, tab, field) {
    const professionalCategory = tab.form.find((form) => form.key?.startsWith("professionalStatus"))?.fields.find((field) => field?.key?.startsWith("professionalCategory"));

    const excludedSectors = ["Ama de casa", "Desempleado", "Estudiante", "Pensionista/Jubilado", "Renta de viudedad", "Rentista", "Otros"];
    if(!excludedSectors.includes(professionalCategory.value)) {
        field.options = store.getters["ProposalsModule/getCompanySector"](field.key).filter(option => !excludedSectors.includes(option.text));
        
        field.value = !excludedSectors.includes(professionalCategory.value) ? field.value : i18n.t("dropdownSelection");
    }
}

function santanderESProfessionalCategoryValidation(form, tab, field) {
    const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
    const nrSalaryPayments = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nrSalaryPayments"));
    const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));
    const dtHiredEnd = tab.form.find((form) => form?.key?.startsWith("professionalStatus")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("DTHiredEndText"));

    // Todas as opções para bloquear
    const excludedOccupations = ["Parado", "Pensionista/Jubilado", "Rentista", "Estudiante", "Otros"];
    // As restantes opções - marteladas - != do formModule porque ainda não temos os "options" carregados neste "field"
    const includedOccupations = ["Fijo", "Temporal", "Asalariado Temporero", "Funcionario", "En practicas", "Fijo discontinuo"];
    professionalStatus.required = field.value !== "Estudiante";

    // Fields required consoante a profissão
    const requiredFieldsKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    const onlyRequiredFieldKeys = ["job", "lastJob", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalCategory?.value);
            }
            field.editable = includedOccupations.includes(professionalCategory?.value);
            field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        return field;
    });

    dtHiredEnd.required = field.text === "Asalariado Temporero" || field.text === "Temporal";

    nrSalaryPayments.required = !excludedOccupations.includes(field.value);
    netReceiptValueAvg.required = !excludedOccupations.includes(field.value);
}

function ptProfessionalCategoryValidation(form, tab, field, idFinancial) {
    const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
    const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));
    const isSickLeave = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("isSickLeave"));

    // Todas as opções para bloquear
    const excludedOccupations = ["Desempregado", "Doméstica", "Domestica", "Estudante", "Reformado", "Pessoa que vive dos rendimentos"];
    // As restantes opções - marteladas - != do formModule porque ainda não temos os "options" carregados neste "field"
    const includedOccupations = ["Função Pública", "Militar / Outras", "Professor", "Quadro médio superior", "Qualificado / Especializado", "Restauração e Hotelaria", "Sector Primário", "Sector Secundário", "Sector Financeiro", "Serviços / Comércio e Administração", "Técnico especializado / Pequeno proprietário"];
    professionalStatus.required = field.value !== "Estudante";
    // Opções para desbloquear última profissão SIFA-1349
    const lastjobException = ["Desempregado", "Reformado", "Pessoa que vive dos rendimentos"];
    // Opções para desbloquear última profissão SIFA-1349
    const dTHiredEndException = ["Prazo", "Temporário"];
    // https://dtogsc.atlassian.net/browse/SIFA-1889
    // Cetelem - Desbloquear o campo Empresa quando a categoria profissional é:005 Outros/Desemp/Reform/Estudante
    const cetelemExceptions = ["Outros", "Desempregado", "Estudante", "Reformado"];

    // Fields required consoante a profissão
    const requiredFieldsKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
    // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
    const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalCategory?.value);
            }
            field.editable = includedOccupations.includes(professionalCategory?.value);
            field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        // Opções para desbloquear última profissão SIFA-1349
        else if (field.key?.startsWith("lastJob")) {
            field.editable = lastjobException.includes(professionalCategory?.value);
            field.required = lastjobException.includes(professionalCategory?.value);
        }
            // Data Fim Actividade - Obrigatoriedade do Campo
        // https://dtogsc.atlassian.net/browse/SIFA-1337
        else if (field.key?.startsWith("DTHiredEndText")) {
            field.required = dTHiredEndException.includes(professionalCategory?.value);
        }
        return field;
    });

    netReceiptValueAvg.required = !excludedOccupations.includes(field?.value);
    isSickLeave.required = !excludedOccupations.includes(field?.value);
    isSickLeave.editable = !excludedOccupations.includes(field?.value);
    if (excludedOccupations.includes(field?.value)) {
        isSickLeave.value = 2;
        isSickLeave.keyReRender++
    }

    // https://dtogsc.atlassian.net/browse/SIFA-1889
    // Cetelem - Desbloquear o campo Empresa quando a categoria profissional é:005 Outros/Desemp/Reform/Estudante
    const companyName = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("companyName"));
    if (cetelemExceptions.includes(field?.value) && companyName && idFinancial === FINANCIAL_IDS.CETELEM) {
        companyName.editable = true;
    }
}

function bbvaPtProfessionalCategoryValidation(form, tab, field) {
    const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
    const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));
    const isSickLeave = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("isSickLeave"));

    // Todas as opções para bloquear
    const excludedOccupations = ["Desempregado", "Pensionista/Reformado", "Estudante"];
    // As restantes opções - marteladas - != do formModule porque ainda não temos os "options" carregados neste "field"
    const includedOccupations = ["Empregado"];
    professionalStatus.required = field.value !== "Estudante";
    // Opções para desbloquear última profissão SIFA-1349
    const lastjobException = ["Desempregado", "Pensionista/Reformado"];
    // Opções para desbloquear última profissão SIFA-1349
    const dTHiredEndException = ["Temporario ou a prazo"];

    // Fields required consoante a profissão
    const requiredFieldsKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
    // const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "companyCAE", "principalActivityCountry"];
    const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalCategory?.value);
            }
            field.editable = includedOccupations.includes(professionalCategory?.value);
            field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        // Opções para desbloquear última profissão SIFA-1349
        else if (field.key?.startsWith("lastJob")) {
            field.editable = lastjobException.includes(professionalCategory?.value);
            field.required = lastjobException.includes(professionalCategory?.value);
        }
            // Data Fim Actividade - Obrigatoriedade do Campo
        // https://dtogsc.atlassian.net/browse/SIFA-1337
        else if (field.key?.startsWith("DTHiredEndText")) {
            field.required = dTHiredEndException.includes(professionalCategory?.value);
        }
        return field;
    });

    netReceiptValueAvg.required = !excludedOccupations.includes(field?.value);
    isSickLeave.required = !excludedOccupations.includes(field?.value);
    isSickLeave.editable = !excludedOccupations.includes(field?.value);
    if (excludedOccupations.includes(field?.value)) {
        isSickLeave.value = 2;
        isSickLeave.keyReRender++
    }
}

function cofidisPtProfessionalCategoryValidation(form, tab, field) {
    const professionalStatus = form.fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
    const netReceiptValueAvg = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("netReceiptValueAvg"));
    const isSickLeave = tab.form.find((form) => form?.key?.startsWith("profit")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("isSickLeave"));

    // Categorias que bloqueiam campos de emprego (inativos/sem vínculo laboral)
    const excludedOccupations = ["Diversos"];

    // Categorias que requerem campos de emprego preenchidos
    // As restantes opções - marteladas - != do formModule porque ainda não temos os "options" carregados neste "field"
    const includedOccupations = ["Trabalhador por Conta Própria", "Publico", "Privado"];
    professionalStatus.required = field.value !== "Diversos";

    // Opções para desbloquear última profissão SIFA-1349
    //const lastjobException = [""];
    const dTHiredEndException = ["Contrato a Prazo de Termo Certo"];

    // Fields required consoante a profissão
    const requiredFieldsKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];
    
    const onlyRequiredFieldKeys = ["job", "companyName", "companyPhone", "companyActivity", "professionalStatus", "DTHiredText", "DTHiredEndText",
        "companyPostalCode1", "companyPostalCode2", "companyCity", "companyNIF", "companyCAE", "principalActivityCountry"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalCategory = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalCategory"));
        if (allRequiredFieldKeys.includes(field.key)) {

            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalCategory?.value);
            }
            field.editable = includedOccupations.includes(professionalCategory?.value);
            field.value = includedOccupations.includes(professionalCategory?.value) ? field.value : "";
        }
        // Opções para desbloquear última profissão SIFA-1349
        else if (field.key?.startsWith("lastJob")) {
            field.editable = true;//lastjobException.includes(professionalCategory?.value);
            field.required = false;//lastjobException.includes(professionalCategory?.value);
        }
            // Data Fim Actividade - Obrigatoriedade do Campo
        // https://dtogsc.atlassian.net/browse/SIFA-1337
        else if (field.key?.startsWith("DTHiredEndText")) {
            field.required = dTHiredEndException.includes(professionalCategory?.value);
        }

        else if (field.key?.startsWith("professionalCategory")) {
            field.label = "pages.ProposalsPage.proposalsForm.participantsData.professionalData.label.professionalCategoryCofidis";
        }
        return field;
    });

    netReceiptValueAvg.required = !excludedOccupations.includes(field?.value);
    isSickLeave.required = !excludedOccupations.includes(field?.value);
    isSickLeave.editable = !excludedOccupations.includes(field?.value);
    if (excludedOccupations.includes(field?.value)) {
        isSickLeave.value = 2;
        isSickLeave.keyReRender++
    }
}

function ptMaritalStateValidation(form, tab, field) {
    const includedOccupations = ["Casado", "CASADO(A)"];

    // As restantes opções
    const excludedOccupations = field?.options?.filter(obj => !includedOccupations.includes(obj.text)).map(obj => obj.text);
    console.log(excludedOccupations);

    const requiredFieldsKeys = ["marriageType"];
    const onlyRequiredFieldKeys = ["marriageType"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("personalInfo"))?.fields.map((field) => {
        const maritalState = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("maritalState"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(maritalState?.value);
            }
            field.editable = includedOccupations.includes(maritalState?.value);
            field.value = includedOccupations.includes(maritalState?.value) ? field.value : "";
        }
        return field;
    });
}

function ptContractTypeValidation(form, tab, field) {
    const includedOccupations = ["Prazo", "Temporário"];
    console.log(field?.value);

    const requiredFieldsKeys = ["DTHiredEndText"];
    const onlyRequiredFieldKeys = ["DTHiredEndText"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalStatus = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalStatus?.value);
            }
            field.value = includedOccupations.includes(professionalStatus?.value) ? field.value : "";
        }
        return field;
    });
}

function cofidisJobValidation(form, tab) {
    const includedJobs = ["Reformados"];

    //TODO verificar melhor
    const notRequiredFieldsKeys = [
        "companyName", "companyPhone", "companyActivity", "professionalStatus",
        "DTHiredText", "DTHiredEndText", "companyPostalCode1", "companyPostalCode2",
        "companyCity", "companyNIF", "companyCAE", "principalActivityCountry",
    ];

    // Inclusão de todas as variantes dos participantes
    const allNotRequiredFieldKeys = addVariations(notRequiredFieldsKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const jobField = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("job"));
        if (allNotRequiredFieldKeys.includes(field.key)) {
            field.required = !includedJobs.includes(jobField?.value);
            field.editable = !includedJobs.includes(jobField?.value);
            field.hasError = false;

            field.value = !includedJobs.includes(jobField?.value) ? field.value : "";

            console.log("jobField?.value", jobField?.value);

            const isDropdownField = field.type === "dropdown";
            const isInvalidValue = includedJobs.includes(jobField?.value);

            if (isDropdownField && isInvalidValue) {
                field?.options?.forEach((option) => {
                option.selected = option?.value === -1;
                });
            }
        }
        return field;
    });
}

function esContractTypeValidation(form, tab, field) {
    const includedOccupations = ["Eventual", "Fin de Obra"];
    console.log(field?.value);

    const requiredFieldsKeys = ["DTHiredEndText"];
    const onlyRequiredFieldKeys = ["DTHiredEndText"];

    // Inclusão de todas as variantes dos participantes
    const allRequiredFieldKeys = addVariations(requiredFieldsKeys);
    const allOnlyRequiredFieldKeys = addVariations(onlyRequiredFieldKeys);

    tab.form.find((form) => form?.key?.startsWith("professionalStatus"))?.fields.map((field) => {
        const professionalStatus = form.fields.find((fieldWithKey) => fieldWithKey?.key?.startsWith("professionalStatus"));
        if (allRequiredFieldKeys.includes(field.key)) {
            if (allOnlyRequiredFieldKeys.includes(field.key)) {
                field.required = includedOccupations.includes(professionalStatus?.value);
            }
            field.value = includedOccupations.includes(professionalStatus?.value) ? field.value : "";
        }
        return field;
    });
}

function handleESProposalIDFields(tab, field, data, formKey, tabKey) {
    // * campos de DNI
    const dniField = tab.form.find((form) => form?.key?.startsWith("personalData")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("BI"));
    const dtExpireCCText = tab.form.find((form) => form?.key?.startsWith("personalData")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("dtExpireCCText"));
    const dtEmissionCCText = tab.form.find((form) => form?.key?.startsWith("personalData")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("dtEmissionCCText"));

    // * campos de NIE
    const neiField = tab.form.find((form) => form?.key?.startsWith("personalData")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nrResidenceAuthorization"));
    const dtExpireResidenceAuthorizationText = tab.form.find((form) => form?.key?.startsWith("personalData")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("dtExpireResidenceAuthorizationText"));
    const dtEmissionResidenceAuthorizationText = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtEmissionResidenceAuthorizationText"));

    const residencePermitTypeField = tab.form.find((form) => form?.key?.startsWith("personalData")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("residencePermitType"));
    const documentTypeDropdown = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("documentType"));

    const isSpain = field.value.toLowerCase() === "españa";

    neiField.required = !isSpain;
    residencePermitTypeField.required = !isSpain;
    dtExpireResidenceAuthorizationText.required = !isSpain;
    dniField.required = isSpain;
    dtExpireCCText.required = isSpain;

    let documentType = getValueFormData(
        data,
        formKey,
        tabKey,
        documentTypeDropdown.key
    );

    if(documentType == '') {
        documentType = isSpain ? 'DNI' : 'NIE';
        documentTypeDropdown.value = documentType;
    }
    documentTypeDropdown.editable = !isSpain;

    if (documentType) {
        switch (documentType) {
            case "DNI":
                setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, dtEmissionCCText }, true, 'ES'); 
                setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText, dtEmissionResidenceAuthorizationText }, false, 'ES');
                break;
            case "NIE":
                setFieldsVisibilityAndRequirements({ dniField, dtExpireCCText, dtEmissionCCText }, false, 'ES');
                setFieldsVisibilityAndRequirements({ neiField, dtExpireResidenceAuthorizationText, dtEmissionResidenceAuthorizationText }, true, 'ES');
                break;
            default: 
                break;
        }
    }

}

function getPTIDFields(tab) {
    // * campos de BI/Cartão de Cidadão
    const nifField = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("NIF"));
    const dniField = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("BI"));
    const dtExpireCCText = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireCCText"));
    const emissionEntityCC = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("emissionEntityCC"));
    const niss = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("NISS"));

    // * campos de Passaporte
    const numberPassport = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nrPassport"));
    const dtExpirePassport = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpirePassportText"));

    // * campos de Titulo de residência
    const neiField = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("nrResidenceAuthorization"));
    const dtExpireResidenceAuthorizationText = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("dtExpireResidenceAuthorizationText"));
    return {
        neiField,
        dtExpireResidenceAuthorizationText,
        nifField,
        dniField,
        dtExpireCCText,
        emissionEntityCC,
        niss,
        numberPassport,
        dtExpirePassport
    };
}

function handlePTProposalIDFields(tab, field, data, formKey, tabKey, idFinancial) {
    const {
        neiField,
        dtExpireResidenceAuthorizationText,
        dniField,
        dtExpireCCText,
        emissionEntityCC,
        numberPassport,
        dtExpirePassport,
        niss,
    } = getPTIDFields(tab);
    const documentTypeDropdown = tab.form.find((form) => form?.key?.startsWith("personalData"))?.fields.find((field) => field?.key?.startsWith("documentType"));
    const isPortugal = field.value.toLowerCase() === "portugal";

    documentTypeDropdown.editable = !isPortugal;
    documentTypeDropdown.value = getValueFormData(
        data,
        formKey,
        tabKey,
        documentTypeDropdown.key
    );

    if (isPortugal) {
        switch (idFinancial) {
            case FINANCIAL_IDS.CREDIBOM:
                niss.required = false;
                break;
        }
    }

    if (documentTypeDropdown?.value) {
        documentTypeDropdown?.options?.forEach((option) => {
            option.selected = option?.value === documentTypeDropdown?.value;
        });
        switch (documentTypeDropdown.value) {
            case "BI/Cartão de Cidadão":
            case "Cartão de Cidadão":
            case "B.I. Civil":
                // BI/CC
                setFieldsVisibilityAndRequirements({dniField, dtExpireCCText, emissionEntityCC}, true);
                // Titulo de residência
                setFieldsVisibilityAndRequirements({neiField, dtExpireResidenceAuthorizationText}, false);
                // Passaporte
                setFieldsVisibilityAndRequirements({numberPassport, dtExpirePassport}, false);
                break;
            case "Passaporte":
                // Passaporte
                setFieldsVisibilityAndRequirements({numberPassport, dtExpirePassport}, true);
                // Titulo de residência
                setFieldsVisibilityAndRequirements({neiField, dtExpireResidenceAuthorizationText}, false);
                // BI/CC
                setFieldsVisibilityAndRequirements({dniField, dtExpireCCText, emissionEntityCC}, false);
                break;
            case "Autorização de residência":
            case "Titulo de residência":
            case "Autorização de residência CPLP":
            case "Autorização de Residência CPLP":
            case "Titulo/Cartão Residência Temporário":
            case "Cartão Cidadão Tratado Porto Seguro":
            case "Certificado de Registo EU":
            case "Titulo/Cartão Residência Permanente":
            case "Autorizacao de Residencia Perm.":
            case "Autorizacao de Residencia Temp.":
            case "Cartão do Cidadão Porto Seguro":
            case "Guia de Residência - Provisório":
                // Titulo de residência
                setFieldsVisibilityAndRequirements({neiField, dtExpireResidenceAuthorizationText}, true);
                // BI/CC
                setFieldsVisibilityAndRequirements({dniField, dtExpireCCText, emissionEntityCC}, false);
                // Passaporte
                setFieldsVisibilityAndRequirements({numberPassport, dtExpirePassport}, false);
                break;
        }
    }
}

function companyPartnerField(cleanParticipantTabForm) {
    const partnerParticipantFields = {
        "label": "pages.ProposalsPage.proposalsForm.participantsData.intervencion.label.quota",
        "type": "text",
        "key": "quota",
        "saveKey": "quota",
        "inputType": "number",
        "stepNumber": "any",
        "maxNumber": 999,
        "inputIcon": "percent",
        "value": "",
        "editable": true,
        "required": false,
        "flexdir": "col",
        "colspan": 2,
        "fieldShow": false,
        "proposalType": [
            "PT"
        ]
    };
    cleanParticipantTabForm?.form[0]?.fields.splice(1, 0, partnerParticipantFields);
}

function addParticipantsTabs(data, lstParticipantsList, formObject, proposalClientType, financialNationality, idFinancial) {
    // First Participant Type
    const isItACompanyForm = proposalClientType.clientTypeId === 3;
    //Particular Participant Form
    delete require.cache[require.resolve('../forms/proposalTabs/tab-4.json')];
    //Company Participant Form
    delete require.cache[require.resolve(`../forms/proposalTabs/tab-4-company.json`)];

    lstParticipantsList[0].saveKey = isItACompanyForm ? "firstClientCompany" : "firstClientPerson";

    // ** Inicio do loop
    for (let i = 2; i <= lstParticipantsList.length; i++) {
        //Particular Participant Form
        delete require.cache[require.resolve('../forms/proposalTabs/tab-4.json')];
        //Company Participant Form
        delete require.cache[require.resolve(`../forms/proposalTabs/tab-4-company.json`)];
        const interventionTypeId = data?.lstParticipants[i - 1]?.idClienttype;
        const isACompanyFirstParticipant = proposalClientType.clientTypeId === 3;
        const isItACompany = interventionTypeId === 3;
        const cleanParticipantTabForm = isItACompany ?
            require(`../forms/proposalTabs/tab-4-company.json`) :
            require('../forms/proposalTabs/tab-4.json');
        // const cleanParticipantTabForm = require('../forms/proposalTabs/tab-4.json');

        if (isACompanyFirstParticipant && !isItACompany) {
            companyPartnerField(cleanParticipantTabForm);
        }

        cleanParticipantTabForm.form = filterOutNotUsedFieldsByNationality(cleanParticipantTabForm.form, financialNationality);

        // adicionar o botão para eliminar participante;
        cleanParticipantTabForm.btns.unshift(
            {
                "key": "deleteTab",
                "bgColor": "#C2C2C2",
                "label": i18n.t(
                    'pages.ProposalsPage.proposalsForm.addNewParticipants.deleteBtnLabel'
                ),
                "icon": "",
                "iconPosition": "",
                "class": "btn-3",
            }
        );

        const participantRelationshipField = {
            "label": "pages.ProposalsPage.proposalsForm.participantsData.intervencion.label.relationship",
            "type": "dropdown",
            "key": "relationToProponent",
            "saveKey": "relationToProponent",
            "value": "",
            "editable": true,
            "required": true,
            "flexdir": "col",
            "colspan": 3,
            "fieldShow": false,
            "searchable": true,
            "proposalType": [
                "PT",
                "ES"
            ],
            "populateOptionsActions": [
                {
                    "type": "action",
                    "name": "ProposalsModule/getRelationshipList",
                    "payload": "finId",
                    "withKey": true
                },
                {
                    "type": "getter",
                    "name": "ProposalsModule/getRelationshipList",
                    "withKey": true
                }
            ],
            "onChangeActions": [
                {
                    "type": "onChangeRelationToProponent",
                    "fields": []
                },
            ],
        };

        if (isACompanyFirstParticipant && idFinancial === FINANCIAL_IDS.BBVA) {
            console.log('entrou no bbva company first participant');
            /**participantRelationshipField.options = [
                {
                    value: -1,
                    text: i18n.t("dropdownSelection"),
                    disabled: true,
                },
                {
                    value: 0,
                    text: "Beneficiário",
                },
                {
                    value: 1,
                    text: "Representante",
                }
            ];

            delete participantRelationshipField.populateOptionsActions;
            */
        }

        const cleanParticipantTabFormCopy = JSON.parse(JSON.stringify(cleanParticipantTabForm));
        const participantRelationshipFieldCopy = JSON.parse(JSON.stringify(participantRelationshipField));

        cleanParticipantTabFormCopy.id = 4 + (i - 1) / 10;
        cleanParticipantTabFormCopy.key = String(i - 1);
        cleanParticipantTabFormCopy.title = `${i}º Titular`;
        
        cleanParticipantTabFormCopy.saveKey = isItACompany ? companyPersonTabMapping[i - 1] : particularPersonTabMapping[i - 1];
        // cleanParticipantTabFormCopy.saveKey = particularPersonTabMapping[i - 1];
        cleanParticipantTabFormCopy.parentSaveKey = "clientInfo";

        cleanParticipantTabFormCopy.form[0].fields.splice(isItACompany ? 2 : 1, 0, participantRelationshipFieldCopy);

        cleanParticipantTabFormCopy.form = concatKeyValues(cleanParticipantTabFormCopy.form, (i - 1));
        cleanParticipantTabFormCopy.dynamicallyCreated = true;

        //item com id 5 porque é o próximo tab "estático"
        const tabFiveIndex = formObject.tabs.findIndex(item => item.id === 5);
        formObject.tabs.splice(tabFiveIndex, 0, cleanParticipantTabFormCopy);
    }
}

function bbvaESDigitalSignatureBtn(lastIdState, form) {
    // Em estado Approved e contract requested
    if ((lastIdState >= 50 && lastIdState < 100)) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "ES",
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "digitalSignatureBBVAES"
                }
            ]
        });
    }
}

function sofincoDigitalSignatureBtn(lastIdState, form) {
    // Em estado Approved e contract requested
    if ((lastIdState >= 50 && lastIdState < 100)) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "ES",
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": lastIdState === 50 ? "digitalSignature" : "digitalSignatureWithPopup"
                }
            ]
        });
    }
}

function credibomDigitalSignatureBtn(lastIdState, form) {
    // Apenas em estado Approved
    if (lastIdState === 50) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "credibomDigitalSignatureWithPopup"
                }
            ]
        });
    }
}

function bbvaPTDigitalSignatureBtn(lastIdState, form, canSign, product, titular) {
    // Apenas em estado Approved
    if (lastIdState === 50 && canSign && titular.idClienttype === 1 && (product === 1 || product === 2)) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "credibomDigitalSignatureWithPopup"
                }
            ]
        });
    } else if ((lastIdState === 60 && titular.idClienttype === 1 && (product === 1 || product === 2)) ||
                ((lastIdState === 50 || lastIdState === 60) && titular.idClienttype !== 1) ||
                ((lastIdState === 50 || lastIdState === 60) && titular.idClienttype === 1 && product === 3)) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "bbvaPTDigitalSignature"
                }
            ]
        });
    } else if (lastIdState === 50 && !canSign) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.updateCarDataBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.updateCarDataBtnV2",
            "key": "updateCarData",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "updateProposalWithCarData"
                }
            ]
        });
    }
}

function caixabankDigitalSignatureBtn(lastIdState, capitalToFinance, form) {
  // Em estado Approved e contract requested
  if ((lastIdState >= 50 && lastIdState < 100) && capitalToFinance <= 39999) {// change capitalToFinance value to 39999, before was 30000
    form.fields.push({
      "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
      "type": "button",
      "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
      "key": "digitalSignature",
      "editable": false,
      "required": false,
      "flexdir": "col",
      "colspan": 4,
      "fieldShow": true,
      "proposalType": [
        "ES",
        "PT"
      ],
      "onChangeActions": [
        {
          "type": "digitalSignatureCaixabank"
        }
      ]
    });
  }
}

function sabadellDigitalSignatureBtn(lastIdState, form) {
    // Em estado Approved e contract requested
    if ((lastIdState >= 50 && lastIdState < 100)) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "ES",
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "digitalSignatureCaixabank"
                }
            ]
        });
    }
}

function showOnlyAMessage(lastIdState, form) {
    // Apenas em estado Approved
    if (lastIdState === 50 || lastIdState === 60) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "showOnlyPopupWithMsg"
                }
            ]
        });
    }
}

function cofidisPTDigitalSignatureBtn(lastIdState, form) {
    // Apenas em estado Approved
    if (lastIdState === 50 || lastIdState === 60) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "cofidisDigitalSignatureWithPopup"
                }
            ]
        });
    }
}

function ptDigitalSignatureBtn(lastIdState, form) {
    // Apenas em estado Approved
    if (lastIdState === 50 || lastIdState === 60) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractSignBtnV2",
            "key": "digitalSignature",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "PT"
            ],
            "onChangeActions": [
                {
                    "type": "ptDigitalSignature"
                }
            ]
        });
    }
}

//Video identification button
function bbvaEsVideoIdentificationBtn(lastIdState, form, data) {
    // Apenas em estado Approved
    if (lastIdState >= 50 && lastIdState < 100 && data?.showBtnVideoIdentification) {
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.videoIdentificationBtn",
            "type": "button",
            "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.videoIdentificationBtnV2",
            "key": "videoIdentification",
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 4,
            "fieldShow": true,
            "proposalType": [
                "ES"
            ],
            "onChangeActions": [
                {
                    "type": "videoIdentificationBtnWithPopup"
                }
            ]
        });
    }

    if (lastIdState >= 10) {
        // Campo com o estado da proposta na Financeira(código fornecido pelos mesmos)
        form.fields.push({
            "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.financialVideoIdentificationStatus",
            "key": "financialVideoIdentificationStatusLabel",
            "type": "text",
            "value": data?.videoIdentificationStatus === "" ? i18n.t("pages.ProposalsPage.proposalsForm.actions.codes.label.financialStatusDefault") : data?.videoIdentificationStatus,
            "editable": false,
            "required": false,
            "flexdir": "col",
            "colspan": 12,
            "fieldShow": true,
            "proposalType": [
                "ES"
            ]
        });
    }
}

function formShaperBasedOnProposalStatus(formObject, lastIdState, capitalToFinance, financialCode, gSCCode, data, financialContractCode, dtActivateContract, idFinancial, history, canSign) {
    const idProfile = store.getters["AuthenticationModule/getIdProfile"];
    formObject.tabs.map((tab) => {
    tab.form.map((form) => {

            if (form.key === "proposalIdentification") {
                const forfeitProposalStatus = [10, 30, 50, 60];
                if (forfeitProposalStatus.includes(lastIdState)) {
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.forfeitProposal",
                        "type": "button",
                        "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.forfeitBtnLabel",
                        "key": "forfeitProposal",
                        "bgColor": "#666666",
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 2,
                        "fieldShow": true,
                        "proposalType": [
                            "ES",
                            "PT"
                        ],
                        "onChangeActions": [
                            {
                                "type": "forfeitProposal"
                            }
                        ]
                    });
                }

                if (history.length > 0) {
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.history",
                        "type": "button",
                        "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.history",
                        "key": "historyBtn",
                        "bgColor": "#666666",
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 2,
                        "fieldShow": true,
                        "proposalType": [
                            "ES",
                            "PT"
                        ],
                        "onChangeActions": [
                            {
                                "type": "historyBtn"
                            }
                        ]
                    });
                }

                if(idProfile === 1) {
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.logs",
                        "type": "button",
                        "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.logs",
                        "key": "logsBtn",
                        "bgColor": "#666666",
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 2,
                        "fieldShow": true,
                        "proposalType": [
                            "ES",
                            "PT"
                        ],
                        "onChangeActions": [
                            {
                                "type": "logsBtn"
                            }
                        ]
                    });
                }

                if(data?.showBtnChangeStatus) {
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.changeProposalStatusLabel",
                        "type": "button",
                        "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.changeProposalStatus",
                        "key": "changeProposalStatusBtn",
                        "bgColor": "#666666",
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 2,
                        "fieldShow": true,
                        "proposalType": [
                            "ES",
                            "PT"
                        ],
                        "onChangeActions": [
                            {
                                "type": "changeProposalStatusBtn"
                            }
                        ]
                    });
                }

                form.fields.push({
                    "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.downloadICDoc",
                    "type": "button",
                    "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.downloadICDocBTN",
                    "key": "docIcBtn",
                    "bgColor": "#666666",
                    "editable": false,
                    "required": false,
                    "flexdir": "col",
                    "colspan": 2,
                    "fieldShow": data?.hasCompanyICDoc || false,
                    "proposalType": [
                        "ES",
                        "PT"
                    ],
                    "onChangeActions": [
                        {
                            "type": "docIcBtn"
                        }
                    ]
                });
            }

            if (form.key === "proposalIdentification2") {
                // SIFA-1415 - Mostrar o deal ID - mesmo quando é tecnicamente rejeitada https://dtogsc.atlassian.net/browse/SIFA-1415
                // https://dtogsc.atlassian.net/browse/SIFA-1482 Credibom - para as financeiras portuguesas mostrar o ID assim que fica em análise e temos a resposta da financeira
                if (lastIdState > 0 && financialCode.length != 0) {
                    // Campo da resposta com o código da financeira após submissão da proposta
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.financialCode",
                        "type": "text",
                        "key": "financialCode",
                        // https://dtogsc.atlassian.net/browse/SIFA-1509
                        // ID da Financeira - Cetelem - não está disponível quando a proposta é aprovada
                        // O Id da Financeira na cetelem é o GSC code (ID da proposta)
                        "value": idFinancial === FINANCIAL_IDS.CETELEM ? gSCCode : financialCode,
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 2,
                        "fieldShow": true,
                        "proposalType": [
                            "PT",
                            "ES"
                        ]
                    });
                }

                if (lastIdState >= 40 || lastIdState === 20) {
                    // Campo com o estado da proposta
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.proposalStatus",
                        "key": "statusLabel",
                        "type": "status-label",
                        "flexdir": "col",
                        "colspan": 2,
                        "status": lastIdState,
                        "fieldShow": true,
                        "proposalType": [
                            "PT",
                            "ES"
                        ]
                    });
                }

                if (lastIdState >= 10){
                    // Campo com o estado da proposta na Financeira(código fornecido pelos mesmos)
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.financialStatus",
                        "key": "financialStatusLabel",
                        "type": "text",
                        "value": data.proposal.lastIdStateFinancial === "" ? i18n.t("pages.ProposalsPage.proposalsForm.actions.codes.label.financialStatusDefault") : data.proposal.lastIdStateFinancial,
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 2,
                        "fieldShow": true,
                        "proposalType": [
                            "PT",
                            "ES"
                        ]
                    });
                }

                const financialContractStatus = data.proposal.financialContractStatus;
                //70 https://dtogsc.atlassian.net/browse/SIFA-1907
                if ((lastIdState >= 50 && lastIdState < 100) && idFinancial == FINANCIAL_IDS.SANTANDER_CONSUMER && financialContractStatus.length != 0) {

                    // Campo com o estado do contrato
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractStatus",
                        "key": "contracstatusLabel",
                        "type": "contrac-status-label",
                        "flexdir": "col",
                        "colspan": 5,
                        "status": financialContractStatus,
                        "fieldShow": true,
                        "proposalType": [
                            "PT"
                        ]
                    });
                }

                // Número do Contrato e Data de ativação do Contrato
                // Número do Contrato e Data de ativação do Contrato
                if ((lastIdState === 90 || lastIdState === 100)) {
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.contractNumber",
                        "type": "text",
                        "key": "financialContractCode",
                        "editable": false,
                        "value": financialContractCode,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 2,
                        "fieldShow": true,
                        "proposalType": [
                            "PT",
                            "ES"
                        ]
                    });
                    let dtActivateContractCopy = dtActivateContract;
                    if (dtActivateContractCopy?.endsWith("Z")) {
                        dtActivateContractCopy = dtActivateContractCopy.slice(0, -1); // remove the last character ("Z")
                    }
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.activationDate",
                        "type": "text",
                        "key": "dtActivateContract",
                        "value": dtActivateContractCopy,
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 3,
                        "fieldShow": true,
                        "proposalType": [
                            "PT",
                            "ES"
                        ]
                    });
                }

                if (data?.proposal?.idFinancial === FINANCIAL_IDS.BPI) {
                    // Campo com o estado da proposta
                    form.fields.push({
                        "label": "pages.ProposalsPage.proposalsForm.actions.codes.label.generateProposalDoc",
                        "type": "button",
                        "fieldLabel": "pages.ProposalsPage.proposalsForm.actions.codes.label.generateProposalDoc",
                        "key": "bpiProposal",
                        "editable": false,
                        "required": false,
                        "flexdir": "col",
                        "colspan": 4,
                        "fieldShow": true,
                        "proposalType": [
                            "PT"
                        ],
                        "onChangeActions": [
                            {
                                "type": "bpiProposalDoc"
                            }
                        ]
                    });
                }

                const titular = data.proposal?.lstparticipantPosition[0];
                const product = data.proposal?.financialData?.idProduct;
                // Assinaturas digitais consoante a financeira
                switch (data?.proposal?.idFinancial) {
                    // Estado Aprovada - Botão de assinatura digital - Apenas em estado APPROVED  - Só caixabank
                    case FINANCIAL_IDS.CAIXABANK:
                        caixabankDigitalSignatureBtn(lastIdState, capitalToFinance, form)
                        break;
                    // Estado Aprovada/contractRequest - Botão de assinatura digital - Apenas em estado APPROVED/CONTRACTREQUEST - Só Sofinco
                    case FINANCIAL_IDS.BBVA_ESPANHA:
                        bbvaESDigitalSignatureBtn(lastIdState, form);

                        // Botão de identificação por vídeo
                        bbvaEsVideoIdentificationBtn(lastIdState, form, data);
                        break;
                    case FINANCIAL_IDS.SOFINCO:
                        sofincoDigitalSignatureBtn(lastIdState, form);
                        break;
                    // Estado Aprovada - Botão de assinatura digital/manual - Apenas em estado APPROVED
                    // Credibom
                    case FINANCIAL_IDS.CREDIBOM:
                        credibomDigitalSignatureBtn(lastIdState, form);
                        break;
                    // Estado Aprovada - Botão de assinatura digital/manual - Apenas em estado APPROVED ou CONTRACTREQUEST para a 2ª via
                    // BBVA PT
                    case FINANCIAL_IDS.BBVA:
                        bbvaPTDigitalSignatureBtn(lastIdState, form, canSign, product, titular);
                        break;
                    //Santander Consumer
                    case FINANCIAL_IDS.SANTANDER_CONSUMER:
                        showOnlyAMessage(lastIdState, form);
                        break;
                    case FINANCIAL_IDS.SABADELL:
                        sabadellDigitalSignatureBtn(lastIdState, form);
                        break;
                    // Estado Aprovada - Botão de assinatura digital/manual - Apenas em estado APPROVED  - Só credibom
                    //CGD
                    //BPI
                    //Cetelem
                    case FINANCIAL_IDS.BPI:
                    case FINANCIAL_IDS.CGD:
                    case FINANCIAL_IDS.CETELEM:
                        ptDigitalSignatureBtn(lastIdState, form);
                        break;
                    case FINANCIAL_IDS.COFIDIS_PT:
                        cofidisPTDigitalSignatureBtn(lastIdState, form);
                        break;
                    default:
                }
            }

            if (lastIdState !== 0 || idProfile === 8) {
                form.fields.map((field) => {
                    field.editable = false;
                    return field;
                });

            
            }
            if (idFinancial === FINANCIAL_IDS.BBVA && lastIdState === 50 && !canSign) {
                form.fields.map((field) => {
                    if (field.key === "carPlate" || field.key === "carChassis" || field.key === "carDtplate"){
                        field.editable = true;
                        //console.log(field)
                    }
                });
            }

        });
    if (lastIdState !== 0 || idProfile === 8) {
      // Campos dos botões diferentes, para uma proposta com o estado diferente de Em Preparação
      const closeForm = {
        "key": "closeForm",
        "bgColor": "#FBA139",
        "label": "pages.ProposalsPage.proposalsForm.actionBtns.closeForm",
        "class": "ml-2 btn-1"
      };
      if (tab.key !== "identification") {
        tab.btns = [
          {
            "key": "nextTab",
            "bgColor": "#008BCD",
            "label": "pages.ProposalsPage.proposalsForm.actionBtns.nextTab",
            "icon": "chevron-right",
            "iconPosition": "right",
            "class": "ml-2 btn-1"
          }
        ]
      } else if(idProfile == 8) {
        tab.btns = [closeForm]
      } else {
        tab.btns = [
           {
            "key": "duplicateProposal",
            "bgColor": "#FBA139",
            "label": "pages.ProposalsPage.proposalsForm.actionBtns.duplicateProposal",
            "class": "btn-3"
          },
          {
            "key": "closeForm",
            "bgColor": "#FBA139",
            "label": "pages.ProposalsPage.proposalsForm.actionBtns.closeForm",
            "class": "ml-2 btn-1"
          }
        ]
      }
    }
  });
}

function handleFormTableToIncludeParticipants(data) {
    const {lstParticipants} = data;

    let updatedLstParticipants = lstParticipants.map(obj => {
        return {
            ...obj.participant,
            interventionType: obj.type,
            idClienttype: obj.idClienttype
        };
    });

    data.proposal.lstParticipants = updatedLstParticipants;

    data.proposal.lstParticipants = data.proposal.lstParticipants.map((participant) => {
        const {address1, address2, personProfit} = participant;
        const {phone1} = participant;
        let nrSalaryPayments = personProfit?.nrSalaryPayments ?? 14;

        // Remove non-digit characters from phone number
        let updatedPhone1 = phone1.replace(/\D/g, "");

        // Truncate to 9 digits if necessary
        if (updatedPhone1.length > 9) {
            updatedPhone1 = updatedPhone1.slice(0, 9);
        }

        const updatedAddress1 = {};
        for (const key in address1) {
            updatedAddress1[`address1_${key}`] = address1[key];
        }

        const updatedAddress2 = {};
        for (const key in address2) {
            updatedAddress2[`address2_${key}`] = address2[key];
        }

        return {
            ...updatedAddress1,
            ...updatedAddress2,
            ...personProfit,
            ...participant,
            ...participant?.companyProfit,
            ...participant?.publicPosition,
            phone1: updatedPhone1,
            nrSalaryPayments: nrSalaryPayments
        };
    });

    //Sufixos em todas as keys de acordo com o index do participante
    Object.keys(data.proposal.lstParticipants).forEach(key => {
        // Skip the first object in the data.proposal.lstParticipantsay
        if (key !== '0') {
            Object.keys(data.proposal.lstParticipants[key]).forEach(innerKey => {
                const suffix = '_' + key;
                data.proposal.lstParticipants[key][innerKey + suffix] = data.proposal.lstParticipants[key][innerKey];
                delete data.proposal.lstParticipants[key][innerKey];
            });
        }
    });

    data.proposal.identification.idFinancial = financialNameById[data?.proposal?.idFinancial];
    data.proposal.financialData.financial = financialNameById[data?.proposal?.idFinancial];

    data.proposal = {...data.proposal, ...data.proposal.lstParticipants};

    delete data.proposal.lstParticipants;
}

function addVariations(keys) {
    const newKeys = [];
    for (const key of keys) {
        for (let i = 1; i <= 5; i++) {
            const newKey = `${key}_${i}`;
            if (!keys.includes(newKey)) {
                newKeys.push(newKey);
            }
        }
    }
    return [...keys, ...newKeys];
}

function addDynamicSuffix(arr, suffix) {
    return arr.map((obj) => {
        return {
            ...obj,
            key: `${obj.key}_` + suffix
        };
    });
}

async function postalCodeHandler(form, financialNationality, populateAction) {
    let address1City = form.fields.find((field) => (field?.key?.startsWith('address1_city')) || (field?.key?.startsWith('address2_city')));
    const address1District = form.fields.find((f) => (f?.key?.startsWith("address1_district")) || (f?.key?.startsWith("address2_district")));
    const address1postalCode = form.fields.find((f) => (f?.key?.startsWith("address1_postalCode1") || f?.key?.startsWith("address2_postalCode1")) && f?.proposalType?.includes(financialNationality));
    const lastIdState = store.getters["ProposalsModule/getProposalStatus"];

    // Para Portugal
    if (financialNationality === "PT") {
        const address1Municipality = form.fields.find((f) => (f?.key?.startsWith("address1_municipality")) || (f?.key?.startsWith("address2_municipality")));
        const address1PostalCode2 = form.fields.find((f) => (f?.key?.startsWith("address1_postalCode2")) || (f?.key?.startsWith("address2_postalCode2")) && f?.proposalType?.includes(financialNationality));

        if (address1postalCode?.maxLength === address1postalCode?.value?.length && address1PostalCode2?.maxLength === address1PostalCode2?.value?.length) {
            const postalCodeValue = address1postalCode?.value + address1PostalCode2?.value;
            const {
                districtValue,
                municipalityValue
            } = await store.dispatch(populateAction.name, {
                formNationality: financialNationality,
                localityFieldKey: address1City?.key,
                postalCode: postalCodeValue
            });
            //Distrito
            const districtTemp = address1District?.value || '';
            address1District.value = (districtValue == '' && districtTemp != '') ? districtTemp : districtValue;
            if (lastIdState === 0) address1District.editable = !districtValue;
            //Municipio
            const municipalityTemp = address1Municipality?.value || '';
            address1Municipality.value = (municipalityValue == '' && municipalityTemp != '') ? municipalityTemp : municipalityValue;
            if (lastIdState === 0) address1Municipality.editable = !municipalityValue;
            //Cidade
            const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](address1City.key);
            if (cityValues.length > 1) {
                store.commit("FormModule/setFieldAttribute", {
                    key: address1City?.key,
                    attribute: 'options',
                    value: cityValues
                });
                store.commit("FormModule/setFieldValue", address1City);
                store.commit("FormModule/setIncrementField", {key: address1City?.key});
            } else {
                address1City.type = "text";
            }

        } else {
            address1City.type = "text";
        }
    }
    // Para Espanha
    else {
        if (address1postalCode?.maxLength === address1postalCode?.value?.length) {
            const {districtValue} = await store.dispatch(populateAction.name, {
                formNationality: financialNationality,
                localityFieldKey: address1City?.key,
                postalCode: address1postalCode?.value
            });
            //Distrito
            address1District.value = districtValue;
            if (lastIdState === 0) address1District.editable = !districtValue;
            //Cidade
            const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](address1City.key);
            if (cityValues.length > 1) {
                store.commit("FormModule/setFieldAttribute", {
                    key: address1City?.key,
                    attribute: 'options',
                    value: cityValues
                });
                store.commit("FormModule/setFieldValue", address1City);
                store.commit("FormModule/setIncrementField", {key: address1City?.key});
            } else {
                address1City.type = "text";
            }
        } else {
            address1City.type = "text";
        }
    
  }
}

async function postalCodePersonCompanyHandler(form, data, formKey, tabKey, financialNationality, populateAction) {
  let companyCity = form.fields.find((f) => f?.key?.startsWith("companyCity"));
  const companyPostalCode1 = form.fields.find((f) => f?.key?.startsWith("companyPostalCode1") && f?.proposalType?.includes(financialNationality));

  companyPostalCode1.value = getValueFormData(
    data,
    formKey,
    tabKey,
    companyPostalCode1.key
  );

  if (companyPostalCode1?.maxLength === companyPostalCode1?.value?.length) {

    await store.dispatch(populateAction.name, { formNationality: financialNationality, localityFieldKey: companyCity?.key, postalCode: companyPostalCode1?.value });

    const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](companyCity.key);
    if (cityValues.length > 1) {
      store.commit("FormModule/setFieldAttribute", { key: companyCity?.key, attribute: 'options', value: cityValues });
      const valueCity = getValueFormData(
        data,
        formKey,
        tabKey,
        companyCity.key
      );
      companyCity.value = valueCity;
      store.commit("FormModule/setIncrementField", { key: companyCity?.key });
    } else {
      companyCity.type = "text";
    }
  } else {
    companyCity.type = "text";
  }
}

async function postalCodeCompanyHandler(form, financialNationality, populateAction) {
    let city = form.fields.find((field) => (field?.key?.startsWith('city')));
    const postalCode1 = form.fields.find((f) => (f?.key?.startsWith("postalCode1")) && f?.proposalType?.includes(financialNationality));
    const lastIdState = store.getters["ProposalsModule/getProposalStatus"];

    if (financialNationality === "PT") {
        const postalCode2 = form.fields.find((f) => (f?.key?.startsWith("postalCode2")) && f?.proposalType?.includes(financialNationality));
        const addressMunicipality = form.fields.find((f) => f?.key?.startsWith("addressMunicipality"));
        const addressDistrict = form.fields.find((f) => f?.key?.startsWith("addressDistrict"));

        if (postalCode1?.maxLength === postalCode1?.value?.length && postalCode2?.maxLength === postalCode2?.value?.length) {
            const postalCodeValue = postalCode1?.value + postalCode2?.value;
            const { districtValue, municipalityValue } = await store.dispatch(populateAction.name, {
                formNationality: financialNationality,
                localityFieldKey: city?.key,
                postalCode: postalCodeValue
            });

             //Distrito
            addressDistrict.value = districtValue;
            if (lastIdState === 0) addressDistrict.editable = !districtValue;
            //Municipio
            addressMunicipality.value = municipalityValue;
            if (lastIdState === 0) addressMunicipality.editable = !municipalityValue;

            //Cidade
            const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](city.key);
            store.commit("FormModule/setFieldAttribute", {key: city?.key, attribute: 'options', value: cityValues});
            store.commit("FormModule/setFieldValue", city);
            store.commit("FormModule/setIncrementField", {key: city?.key});
        }
    } else {
        if (postalCode1?.maxLength === postalCode1?.value?.length) {
            await store.dispatch(populateAction.name, {
                formNationality: financialNationality,
                localityFieldKey: city?.key,
                postalCode: postalCode1?.value
            });
            //Cidade
            const cityValues = store.getters["ProposalsModule/getSearchByPostalCode"](city.key);
            store.commit("FormModule/setFieldAttribute", {key: city?.key, attribute: 'options', value: cityValues});
            store.commit("FormModule/setFieldValue", city);
            store.commit("FormModule/setIncrementField", {key: city?.key});
        }
    }
}

async function mailPostalCodeCompanyHandler(form, financialNationality, populateAction) {
    let mailCity = form.fields.find((field) => (field?.key?.startsWith('mailCity')));
    const mailPostalCode1 = form.fields.find((f) => (f?.key?.startsWith("mailPostalCode1")) && f?.proposalType?.includes(financialNationality));

    if (financialNationality === "PT") {
        const mailPostalCode2 = form.fields.find((f) => (f?.key?.startsWith("mailPostalCode2")) && f?.proposalType?.includes(financialNationality));

        if (mailPostalCode1?.maxLength === mailPostalCode1?.value?.length && mailPostalCode2?.maxLength === mailPostalCode2?.value?.length) {
            const postalCodeValue = mailPostalCode1?.value + mailPostalCode2?.value;
            await store.dispatch(populateAction.name, {
                formNationality: financialNationality,
                localityFieldKey: mailCity?.key,
                postalCode: postalCodeValue
            });
            //Cidade
            const mailCityValues = store.getters["ProposalsModule/getSearchByPostalCode"](mailCity.key);
            store.commit("FormModule/setFieldAttribute", {
                key: mailCity?.key,
                attribute: 'options',
                value: mailCityValues
            });
            store.commit("FormModule/setFieldValue", mailCity);
            store.commit("FormModule/setIncrementField", {key: mailCity?.key});
        }
    } else {
        if (mailPostalCode1?.maxLength === mailPostalCode1?.value?.length) {
            await store.dispatch(populateAction.name, {
                formNationality: financialNationality,
                localityFieldKey: mailCity?.key,
                postalCode: mailPostalCode1?.value
            });
            //Cidade
            const mailCityValues = store.getters["ProposalsModule/getSearchByPostalCode"](mailCity.key);
            store.commit("FormModule/setFieldAttribute", {
                key: mailCity?.key,
                attribute: 'options',
                value: mailCityValues
            });
            store.commit("FormModule/setFieldValue", mailCity);
            store.commit("FormModule/setIncrementField", {key: mailCity?.key});
        }
    }
}

async function mailPostalCodePersonCompanyHandler(form, financialNationality, populateAction) {
    let companyCity = form.fields.find((field) => (field?.key?.startsWith('companyCity')));
    const companyPostalCode1 = form.fields.find((f) => (f?.key?.startsWith("companyPostalCode1")) && f?.proposalType?.includes(financialNationality));

    if (financialNationality === "PT") {
        const companyPostalCode2 = form.fields.find((f) => (f?.key?.startsWith("companyPostalCode2")) && f?.proposalType?.includes(financialNationality));

        if (companyPostalCode1?.maxLength === companyPostalCode1?.value?.length && companyPostalCode2?.maxLength === companyPostalCode2?.value?.length) {
            const postalCodeValue = companyPostalCode1?.value + companyPostalCode2?.value;
            await store.dispatch(populateAction.name, {
                formNationality: financialNationality,
                localityFieldKey: companyCity?.key,
                postalCode: postalCodeValue
            });
            //Cidade
            const companyCityValues = store.getters["ProposalsModule/getSearchByPostalCode"](companyCity.key);
            store.commit("FormModule/setFieldAttribute", {
                key: companyCity?.key,
                attribute: 'options',
                value: companyCityValues
            });
            store.commit("FormModule/setFieldValue", companyCity);
            store.commit("FormModule/setIncrementField", {key: companyCity?.key});
        }
    }
}

function filterOutNotUsedFieldsByNationality(formObject, financialNationality) {
    return formObject.map((formItem) => {
        const filteredFields = formItem?.fields?.filter((field) => field?.proposalType?.some((type) => type?.includes(financialNationality))
        );
        return {...formItem, fields: filteredFields};
    });
}

function formMapperHandler(form, idFinancial, data, formKey, tabKey, financialNationality, clientType, isENIType, tab, newParticipantTab = false, interventionType = null) {
    // RGPD dinâmico consoante a financeira
    if (form?.key?.startsWith("rgpd") && rgpdLabelsByFinancial[idFinancial]) {
        form.fields = tab.dynamicallyCreated ? addDynamicSuffix(rgpdLabelsByFinancial[idFinancial], tab.key) : rgpdLabelsByFinancial[idFinancial];
    }

    const isEniTypeValue = newParticipantTab ? interventionType : data?.lstParticipants[tabKey]?.idClienttype;
    const isENITypeX = isEniTypeValue ? isEniTypeValue === 2 : isENIType;

    form.fields.forEach(async (field) => {
        // adicionar on change actions para as dropdowns do idFinancial 13 - Sofinco / 16 Celetem Spain / 19 - Cofidis PT
        // Apenas Sofinco e Cetelem Spain
        if (hasJobCategory(idFinancial) && field.key.startsWith('job')) {
            field.populateOptionsActions = [{
                "type": "action",
                "name": "ProposalsModule/getJobsWithCategory",
                "payload": "finId & jobCategory",
                "withKey": true
            }, {
                "type": "getter",
                "name": "ProposalsModule/getJobsWithCategory",
                "withKey": true
            }];
        }

        if ((idFinancial === FINANCIAL_IDS.CAIXABANK) && field.key.startsWith('job')) {
            field.populateOptionsActions = [{
                "type": "action",
                "name": "ProposalsModule/getJobs",
                "payload": "finId & clientType",
                "withKey": true
            }, {
                "type": "getter",
                "name": "ProposalsModule/getJobs",
                "withKey": true
            }];
        }

        if (hasJobCategory(idFinancial) && field.key.startsWith('professionalCategory')) {
            field.onChangeActions = [{
                "type": "fetchJobs",
                "fields": []
            }, {
                "type": "enableDisableContractType",
                "fields": []
            }];
        }

    // se for um componente dropdown, popular as opções
    if ((field.type === "dropdown" || field.type === "multi-select") && field.populateOptionsActions) {
        if (field.populateOptionsActions) {
            field.populateOptionsActions.map(async (populateAction) => {
                if (populateAction.type === "action") {
                    switch (populateAction.payload) {
                        case "finId": {
                            await store.dispatch(populateAction.name, { finId: idFinancial, fieldKey: field.key });
                            // console.group("TEST");
                            //console.log(field.key);
                            // console.groupEnd();
                            break;
                        }
                        case "finId & clientType":
                            await store.dispatch(populateAction.name, { finId: idFinancial, fieldKey: field.key, clientType: isEniTypeValue });
                            break;
                        case "finId & jobCategory": {
                            let professionalCategory = form.fields.find((field) => field?.key?.startsWith('professionalCategory'));
                            
                            let profCategoryValue = professionalCategory.value;
                            if(idFinancial == FINANCIAL_IDS.CETELEM_ES && isEniTypeValue == 4 && profCategoryValue == 'Rentista') {
                                profCategoryValue = profCategoryValue + "-" + isEniTypeValue;
                            }
                            
                            const professionalJobs = form.fields.find((f) => f?.key?.startsWith("job"));
                            const optionsProfessionalJobs = await store.dispatch(populateAction.name, { finId: idFinancial, jobCategory: profCategoryValue, fieldKey: field.key });
                            professionalJobs.required = optionsProfessionalJobs.length !== 1;
                            const lastIdState = store.getters["ProposalsModule/getProposalStatus"];
                            if (lastIdState === 0) professionalJobs.editable = optionsProfessionalJobs.length !== 1;
                            if (optionsProfessionalJobs.length === 1)
                                professionalJobs.hasLoading = false;
                            break;
                        }
                        case "postalCode":
                            await postalCodeHandler(form, financialNationality, populateAction);
                            break;
                        case "postalCodeCompany":
                            await postalCodeCompanyHandler(form, financialNationality, populateAction);
                            break;
                        case "mailPostalCodeCompany":
                            await mailPostalCodeCompanyHandler(form, financialNationality, populateAction);
                            break;
                        case "postalCodePersonCompany":
                            await mailPostalCodePersonCompanyHandler(form, financialNationality, populateAction);
                            break;
                        case"postalCodePersonInfoCompany":
                            await postalCodePersonCompanyHandler(form, data, formKey, tabKey, financialNationality, populateAction)
                            break;
                        default:{
                            //populateAction.payload ?
                            //    await store.dispatch(populateAction.name, populateAction.payload) : populateAction.withKey ?
                            //    await store.dispatch(populateAction.name, { fieldKey: field.key }) : await store.dispatch(populateAction.name);
                            await executePopulateAction(populateAction, field.key);
                            break;
                        }
                    } 
                }

                    // FYI - Porquê a property withKey? De forma a diferenciar os getters com um parametro key (referente ao campo de input) e sem parametro. Deve ser adicionado populateAction
                    // tanto na action como no getter.
                    field.options = populateAction.withKey
                        ? store.getters[populateAction.name](field.key)
                        : store.getters[populateAction.name];

                    if(field.key?.startsWith("companyActivity") && field.options &&idFinancial === FINANCIAL_IDS.BBVA_ESPANHA) {
                        bbvaESCompanySectorValidation(form, tab, field);
                    }

                });
            }
        }

        if (field.type === "toggle") {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(
                data,
                formKey,
                tabKey,
                fieldKey
            );
            field.value = fieldValue ? "S" : "N";
        } else if (field.type !== "text-label") {
            const fieldKey = field.key;
            field.value = getValueFormData(data, formKey, tabKey, fieldKey);
            //console.log(fieldKey, field.value);
        }

        if (field.key.startsWith("relationToProponent") && idFinancial === FINANCIAL_IDS.BBVA){
            let interventionTypeField = form.fields.find((field) => field?.key?.startsWith('interventionType'));
            
            const interventionType = getValueFormData(data, formKey, tabKey, interventionTypeField.key);
            console.log('interventionType: ', interventionType);
            //interventionType == 'Avalista' isEniTypeValue == 3
            const fieldKey = field.key;
            field.value = getValueFormData(data, formKey, tabKey, fieldKey);
            if (!(field.value == "Beneficiário" || field.value == "Representante") && isEniTypeValue == 3) {
                field.value = "Selecione uma opção";
            }
        }

        if (field.key.startsWith("beneficialOwner")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(
                data,
                formKey,
                tabKey,
                fieldKey
            );
            field.value = fieldValue === "S" || !fieldValue ? 1 : 2;
        }

        if (field.key.startsWith("carService")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(
                data,
                formKey,
                tabKey,
                fieldKey
            );
            field.value = fieldValue === "S" || !fieldValue ? 1 : 2;
        }

        if (field.key.startsWith("isSickLeave")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(
                data,
                formKey,
                tabKey,
                fieldKey
            );
            field.value = fieldValue === "S" ? 1 : 2;
        }

        if (field.key.startsWith("hasFullProofIncome")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(
                data,
                formKey,
                tabKey,
                fieldKey
            );
            field.value = fieldValue === "N" ? 2 : 1;
        }

        if (field.key.startsWith("specialRequest")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(
                data,
                formKey,
                tabKey,
                fieldKey
            );
            field.value = fieldValue === "S" || fieldValue === true ? 1 : 2;
        }

        if (form?.key?.startsWith("oficialDataToggle")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(
                data,
                formKey,
                tabKey,
                fieldKey
            );
            field.value = fieldValue === "S" || !fieldValue ? 1 : 2;
        }

        if (field?.key?.startsWith("countryActivities")) {
            // Handle special case: O countryActivities vem num formato estranho do backend com '#'.
            // Neste if retira os '#' e coloca em array os valores
            let countryActivities = form.fields.find((field) => field?.key?.startsWith('countryActivities'));
            const countryActivitiesValue = getValueFormData(
                data,
                formKey,
                tabKey,
                countryActivities.key
            );
            const str = countryActivitiesValue;
            const arr = str.split("##");
            countryActivities.value = str === "" ? "" : arr;
        }

        //key definida
        if (form?.key?.startsWith("taxAddress")) {
            let address1SameAddress2 = form.fields.find((field) => field?.key?.startsWith('address1SameAddress2'));
            const address1SameAddress2Value = getValueFormData(
                data,
                formKey,
                tabKey,
                address1SameAddress2.key
            );

            address1SameAddress2.value = address1SameAddress2Value;

            form.fields = form.fields.map((field) => {
                if (field.type !== "neo-toggle")
                    field.value = address1SameAddress2.value === 1 ? "" : field.value;
                field.editable = address1SameAddress2.value !== 1;

                // ! Temporário - remover if (field.key.startsWith("address2_country") && address1SameAddress2.value !== 1)
                if (field.key.startsWith("address2_country") && address1SameAddress2.value !== 1) {
                    field.value = "Portugal";
                    field.keyReRender++;
                }
                return field;
            });

            address1SameAddress2.editable = true;
        }

        // Set Entity on loading proposal form
        if (field.key === "canvasserIdEntity") {
            store.commit("AuthenticationModule/setUserEntityId", field.value);
        }

        // Definição IBAN Dropdown
        if (field.key === "isNational") {
            const fieldKey = field.key;
            const isNational = getValueFormData(data, formKey, tabKey, fieldKey);
            const NIBValue = getValueFormData(data, formKey, tabKey, "NIB");

            if (NIBValue) {
                const nationalityFlag = (NIBValue && (NIBValue.startsWith("PT50"))) ? "PT" :
                    (NIBValue && (NIBValue.startsWith("ES"))) ? "ES" : "";

                field.value = isNational ? "Nacional" : "Internacional";

                store.commit("FormModule/setActiveBankInfo", {
                    value: isNational ? 1 : 2,
                    nationality: nationalityFlag
                });
            }
        }

        // Definição IBAN
        if (field.key === "NIB") {
            const fieldKey = field.key;
            const NIBValue = getValueFormData(data, formKey, tabKey, fieldKey);

            if (NIBValue && (NIBValue.startsWith("PT50") || NIBValue.startsWith("ES"))) {
                field.value = NIBValue.replace(/PT50|ES/g, "");
            }
        }

        // Definir obrigatoriedade de matricula consoante o estado do veiculo
        if (field.key === "i18nCarType") {
            const carType = form.fields.find((fieldWithKey) => fieldWithKey.key === "carType")?.value;
            field.value = (carType === "Usado" || financialNationality === 'PT') ? carType : i18n.t('pages.ProposalsPage.proposalsForm.carData.vehicleData.carTypeValue.new')
        }

        if (field.key === "carType" && field.value === "Usado") {
            const carPlateField = form.fields.find((fieldWithKey) => fieldWithKey.key === "carPlate");
            const carDtplateField = form.fields.find((fieldWithKey) => fieldWithKey.key === "carDtplate");
            const carChassis = form.fields.find((fieldWithKey) => fieldWithKey.key === "carChassis");
            const carService = form?.fields?.find((field) => field.key === 'carService');

            carPlateField.required = true;
            carDtplateField.required = true;
            carService.required = true;
            carService.editable = true;
            // https://dtogsc.atlassian.net/browse/SIFA-1564
            // Feedback tests - Proposta PT - Se a viatura for usada o chassis não é obrigatório
            if (financialNationality === "PT") {
                carChassis.required = false;
                carPlateField.placeholder = "XX-XX-XX";
                carPlateField.mask = "XX-XX-XX";
            } else {
                carChassis.required = true;
            }
                
        }

        const carTypeField = form.fields.find((fieldWithKey) => fieldWithKey.key === "carType");
        if (field.key === "carDtplate" && carTypeField.value === "Usado" && field.value === undefined) {
            const month = String(form.fields.find((fieldWithKey) => fieldWithKey.key === "carMonth").value);
            const carMonth = month.length === 1 ? "0" + month : month;
            const carYear = String(form.fields.find((fieldWithKey) => fieldWithKey.key === "carYear").value);
            const carDtplate = carYear + "-" + carMonth + "-" + "01";
            field.value = carDtplate;
        }

        if (carTypeField?.value === "Novo") {
            const carDtplate = form.fields.find((fieldWithKey) => fieldWithKey.key === "carDtplate");
            carDtplate.required = false;
        }

        // concatena o prefixo dos contactos consoante o form nationality
        if (field.key.startsWith("phone1") || field.key.startsWith("phone2") || field.key.startsWith("companyPhone")) {
            field.prefixValue = financialNationality === 'PT' ? "+351" : "+34";
        }

        // Autopreencher triggers da categoria profissional
        if (field?.key?.startsWith("professionalCategory")) {
            switch (idFinancial) {
                //Sofinco
                case FINANCIAL_IDS.SOFINCO:
                    sofincoProfessionalCategoryValidation(form, tab, field);
                    break;
                //BBVA ES
                case FINANCIAL_IDS.BBVA_ESPANHA:
                    bbvaESProfessionalCategoryValidation(form, tab, field);
                    break;
                //Caixabank
                case FINANCIAL_IDS.CAIXABANK:
                    caixabankProfessionalCategoryValidation(form, tab, field);
                    break;
                //Celetem Spain
                case FINANCIAL_IDS.CETELEM_ES:
                    cetelemSpainProfessionalCategoryValidation(form, tab, field);
                    break;
                //PT cases
                case FINANCIAL_IDS.BBVA:
                    bbvaPtProfessionalCategoryValidation(form, tab, field);
                    break;
                case FINANCIAL_IDS.COFIDIS_PT:
                    cofidisPtProfessionalCategoryValidation(form, tab, field);
                    break;
                case FINANCIAL_IDS.SABADELL:
                    sabadellProfessionalCategoryValidation(form, tab, field);
                    break;
                case FINANCIAL_IDS.SANTANDER_CONSUMER_ES:
                    santanderESProfessionalCategoryValidation(form, tab, field);
                    break;
                case FINANCIAL_IDS.SANTANDER_CONSUMER:
                case FINANCIAL_IDS.BPI:
                case FINANCIAL_IDS.CGD:
                case FINANCIAL_IDS.CETELEM:
                case FINANCIAL_IDS.CREDIBOM:
                    ptProfessionalCategoryValidation(form, tab, field, idFinancial);
                    break;
            }
        }

        // Autopreencher triggers do tipo de cassamento
        if (field?.key?.startsWith("maritalState")) {
            switch (idFinancial) {
                //Sofinco
                case FINANCIAL_IDS.SOFINCO:
                    break;
                //BBVA ES
                case FINANCIAL_IDS.BBVA_ESPANHA:
                  break;
                //Caixabank
                case FINANCIAL_IDS.CAIXABANK:
                    break;
                case FINANCIAL_IDS.CETELEM_ES:
                    break;
                //Caixabank
                //PT cases
                case FINANCIAL_IDS.BBVA:
                case FINANCIAL_IDS.SANTANDER_CONSUMER:
                case FINANCIAL_IDS.BPI:
                case FINANCIAL_IDS.CGD:
                case FINANCIAL_IDS.CETELEM:
                case FINANCIAL_IDS.CREDIBOM:
                case FINANCIAL_IDS.COFIDIS_PT:
                    ptMaritalStateValidation(form, tab, field);
                    break;
            }
        }

        // Autopreencher triggers do tipo de contrato
        if (field?.key?.startsWith("professionalStatus")) {
            switch (idFinancial) {
                //Sofinco
                case FINANCIAL_IDS.SOFINCO:
                    break;
                //BBVA ES
                case FINANCIAL_IDS.BBVA_ESPANHA:
                    break;
                //Caixabank
                case FINANCIAL_IDS.CAIXABANK:
                    break;
                case FINANCIAL_IDS.CETELEM_ES:
                    esContractTypeValidation(form, tab, field);
                    break;
                //PT cases
                case FINANCIAL_IDS.BBVA:
                case FINANCIAL_IDS.SANTANDER_CONSUMER:
                case FINANCIAL_IDS.BPI:
                case FINANCIAL_IDS.CGD:
                case FINANCIAL_IDS.CETELEM:
                case FINANCIAL_IDS.CREDIBOM:
                case FINANCIAL_IDS.COFIDIS_PT:
                    ptContractTypeValidation(form, tab, field);
                    break;
            }
        }

        // Validação Financeira espanhola
        if (field.key.startsWith("nationality") && !field.key.startsWith("nationalityRegime") && field.value) {
            const generalDataSection = tab?.form?.find((formFound) => formFound?.key?.startsWith('generalData'));
            const firstName = generalDataSection?.fields?.find((field) => field?.key?.startsWith('firstName'));
            const firstSurname = generalDataSection?.fields?.find((field) => field?.key?.startsWith('firstSurname'));
            const secondSurname = generalDataSection?.fields?.find((field) => field?.key?.startsWith('secondSurname'));

            switch (financialNationality) {
                case "ES":
                    if (field.value === "Espanha" || field.value === "ESPANHA" || field.value === "España" || field.value === "ESPAÑA") {
                        firstName.required = true;
                        firstSurname.required = true;
                        secondSurname.required = true;
                    } else {
                        //Reset
                        firstName.required = true;
                        firstSurname.required = true;
                        secondSurname.required = false;
                    }
                    //SIFA-687
                    handleESProposalIDFields(tab, field, data, formKey, tabKey);
                    break;
                case "PT":
                    if (field.value === "Espanha" || field.value === "ESPANHA" || field.value === "España" || field.value === "ESPAÑA") {
                        firstName.required = true;
                        firstSurname.required = true;
                        secondSurname.required = true;
                    } else {
                        //Reset
                        firstName.required = true;
                        firstSurname.required = false;
                        secondSurname.required = true;
                    }
                    handlePTProposalIDFields(tab, field, data, formKey, tabKey, idFinancial);
                    break;
            }
        }

        if (field.key.startsWith("interventionType")) {
            field.editable = field.key !== "interventionType";
        }

        //set default properties
        field.hasError = field.hasError || false;
        field.editable = field.editable || false;
        field.required = field.required || false;
        field.loading = field.loading || false;
        field.keyReRender = 1;

        // Mostrar os campos apenas para ENI (Particular/Empresa)
        if (form?.key?.startsWith("eniData")) {
            field.fieldShow = isENITypeX;
            if (!isENITypeX) {
                // ! Comentado para testar
                // field.key = "";
                // field.saveKey = "";
            }
        }

            // https://dtogsc.atlassian.net/browse/SIFA-1567
            // Os PT - ENI - adicionar nos Rendimentos um campo: Valor Facturação Anual - junto ao rendimento liquido mensal.
            // ! revert de lógica
            // https://dtogsc.atlassian.net/browse/SIFA-1683
            // O campo: Valor de Facturação Anual deve também estar disponível no formulário de Particular (não obrigatório)
            // else if (form?.key?.startsWith("profit") && field?.key?.startsWith("receiptValueAnual")) {
            //   field.fieldShow = isENITypeX;
        // }
        else {
            field.fieldShow = field?.proposalType?.includes(financialNationality);
        }

        if (newParticipantTab) field.keyReRender += 1;

        // Bloquear RGPD para BBVA ES
        if (form?.key?.startsWith("rgpd")) {
            field.fieldShow = rgpdLabelsByFinancial[idFinancial] ? true : false;
            field.editable = rgpdLabelsByFinancial[idFinancial] ? true : false;

            // Alteração - Removido o default da primeira opção do RGPD
            // if (field.firstRGPD) {
            //   field.value = field.value === 'S' ? 1 : field.value === "N" ? 2 : 1;
            // } else {
            //   field.value = field.value === 'S' ? 1 : 2;
            // }

            // Alteração - Todas as opções começam a "Não"
            field.value = field.value === 'S' ? 1 : 2;
        }

        if (field?.key?.startsWith("documentType") && field?.value === '' && financialNationality == 'ES') {
            const fieldValue = getValueFormData(data, formKey, tabKey, field.key);
            console.log('fieldValue:: ', field?.value, fieldValue);
            const nationalityField = tab.form.find((form) => form?.key?.startsWith("personalData")).fields?.find((fieldWithKey) => fieldWithKey?.key?.startsWith("nationality"));
            const isSpain = nationalityField?.value?.toLowerCase() === "españa";
            field.value = isSpain ? 'DNI' : 'NIE';
        }

        if (form?.key?.startsWith("publicPosition")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(data, formKey, tabKey, fieldKey);

            if (field?.type === "neo-toggle") {
                field.value = fieldValue === 'S' ? 1 : 2;
            } else {
                field.value = fieldValue;
            }

            const politicalPositionDescKeys = {
                "itself": "itselfDescription",
                "family": "familyDescription",
                "relationswith": "relationswithDescription",
                "otherPosition": "otherPositionDescription"
            };

            form?.fields?.forEach((field) => {
                if (field?.type === "neo-toggle") {
                    const fieldKey = field?.key;
                    const matchingKeys = Object.keys(politicalPositionDescKeys)
                        .filter(key => fieldKey && (key === fieldKey || fieldKey.startsWith(key)));

                    matchingKeys.forEach((key) => {
                        const fieldDescriptionKey = politicalPositionDescKeys[key];
                        const fieldDescription = form.fields.find((f) => f?.key?.startsWith(fieldDescriptionKey));

                        if (fieldDescription) {
                            fieldDescription.required = field?.value === 1;
                        }
                    });
                }
            });
        }

        if (field.key.startsWith("idClienttype")) {
            // Nunca estar visível
            field.fieldShow = false;
            field.value = isEniTypeValue;
        }

        if (field.key.startsWith("nibHolder")) {
            const fieldKey = field.key;
            const fieldValue = getValueFormData(data, formKey, tabKey, fieldKey);
            field.value = i18n.t("pages.ProposalsPage.proposalsForm.bankingInfo.bankData.nibHolder."+nibHolderParticipantMap[fieldValue]);
            if (financialNationality === 'ES' && !(idFinancial == FINANCIAL_IDS.BBVA_ESPANHA || idFinancial == FINANCIAL_IDS.SANTANDER_CONSUMER_ES)) {
                field.fieldShow = false;
            }
        }

        //SIFA-2205 -firstSurname non required
        //if (field.key?.startsWith("firstSurname")) {
        //  field.required = financialNationality === "ES";
        //}

        // Empresa cotada em bolsa - sim ou não.
        // Abre um campo - qual é o nome da bolsa.
        // para a Cetelem enviar no serviço: listedCompanyMarketNameCode - se for sim - o campo - Empresa cotada em bolsa
        // https://dtogsc.atlassian.net/browse/SIFA-1116
        if (field?.key?.startsWith("sickLeaveReason")) {
            const fieldKey = field.key;
            const sickLeaveReasonValue = getValueFormData(data, formKey, tabKey, fieldKey);
            field.value = sickLeaveReasonValue;
            const isSickLeave = form.fields.find((field) => field?.key?.startsWith('isSickLeave'));

            field.fieldShow = isSickLeave?.value === 1;
            field.required = isSickLeave?.value === 1;
            field.value = isSickLeave?.value === 1 ? field?.value : "";
            field.proposalType = isSickLeave?.value === 1 ? field.proposalType : [];
        }

    if (field?.key?.startsWith("DTHiredEndText")) {
      switch(idFinancial){
        case FINANCIAL_IDS.BBVA_ESPANHA:
        case FINANCIAL_IDS.SOFINCO:
          field.required = false;
          field.fieldShow = false;
          break;
        default:
          break;
      }
    }

    if (field?.key?.startsWith("companyPostalCode1")) {
      switch(idFinancial){
        case FINANCIAL_IDS.BBVA_ESPANHA:
        case FINANCIAL_IDS.SOFINCO:
          field.required = false;
          field.fieldShow = false;
          break;
        default:
          break;
      }
    }

    if (field?.key?.startsWith("residencePermitType")) {
        switch(idFinancial){
          case FINANCIAL_IDS.BBVA_ESPANHA:
          case FINANCIAL_IDS.SOFINCO:
          case FINANCIAL_IDS.CAIXABANK:
          case FINANCIAL_IDS.SABADELL:
            field.required = false;
            field.fieldShow = false;
            break;
          default:
            break;
        }
    }

    if (field?.key?.startsWith("phone2")) {
        switch(idFinancial) {
            case FINANCIAL_IDS.BBVA_ESPANHA:
            case FINANCIAL_IDS.SOFINCO:
            case FINANCIAL_IDS.CAIXABANK:
                field.required = false;
                field.fieldShow = false;
                break;
            case FINANCIAL_IDS.SABADELL:
                field.required = false;
                break;
            default:
                break;
        }
    }

    if (field?.key?.startsWith("companyCity")) {
      switch(idFinancial){
        case FINANCIAL_IDS.BBVA_ESPANHA:
        case FINANCIAL_IDS.SOFINCO:
          field.required = false;
          field.fieldShow = false;
          break;
        default:
          break;
      }
    }

    if (field?.key?.startsWith("profitsOrigin")) {
      switch(idFinancial){
        case FINANCIAL_IDS.BBVA_ESPANHA:
        case FINANCIAL_IDS.SOFINCO:
          field.required = false;
          field.fieldShow = true;
          break;
        default:
          break;
      }
    }

    if (field?.key?.startsWith("dateOfFirstExpiry")) {
        switch(idFinancial){
            case FINANCIAL_IDS.CAIXABANK:
                field.required = true;
                field.fieldShow = true;
                break;
            case FINANCIAL_IDS.SABADELL:
                field.required = true;
                field.fieldShow = true;
                break;
            case FINANCIAL_IDS.SANTANDER_CONSUMER_ES:
                field.required = true;
                field.fieldShow = true;
                break;
            case FINANCIAL_IDS.COFIDIS_PT:
                field.required = true;
                field.fieldShow = true;
                break;
            default:
                field.required = false;
                field.fieldShow = false;
                break;
        }
    }
    
    if (field.key.startsWith("professionalStatus") && idFinancial === FINANCIAL_IDS.SABADELL) {
        // Nunca estar visível
        field.required = false;
        field.fieldShow = false;
    }

    if (field.key.startsWith("document") && idFinancial === FINANCIAL_IDS.CETELEM_ES) {
        //SIFA-3095 - Cetelem ES - Esconder os documentos enquanto não existir consumo do serviço de documentação
        field.required = false;
        field.fieldShow = false;
    }

    if (field.key.startsWith("job") && idFinancial === FINANCIAL_IDS.SABADELL) {
        sabadellCompanySectorValidation(form, tab)
    }

    if (field.key.startsWith("job") && idFinancial === FINANCIAL_IDS.COFIDIS_PT) {
        cofidisJobValidation(form, tab)
    }

    if (field?.key?.startsWith("companyNIF")) {
        if (financialNationality === "ES" && !(idFinancial == FINANCIAL_IDS.SABADELL || idFinancial == FINANCIAL_IDS.SANTANDER_CONSUMER_ES)) {
            field.required = false;
            field.fieldShow = false;
        } else if (idFinancial === FINANCIAL_IDS.SABADELL || idFinancial === FINANCIAL_IDS.SANTANDER_CONSUMER_ES) {
            delete field.inputType;
            delete field.stepNumber;
            delete field.maxNumber;
        }

    }

    if (field?.key?.startsWith("companyCAE")) {
        if (financialNationality === "ES" && idFinancial !== FINANCIAL_IDS.SABADELL) {
            field.required = false;
            field.fieldShow = false;
        } else if (idFinancial === FINANCIAL_IDS.SABADELL) {
            const tabClientType = tabKey == 1 ? data?.lstParticipants[tabKey]?.idClienttype : clientType.clientTypeId;

            console.log('companyCAE field validation (tabClientType) ', tabClientType);

            field.required = tabClientType == 4 || false;
            field.fieldShow = tabClientType == 4 || false;
        }

    }

    if (field.key === "carType") {
        field.fieldShow = false
    }

    });

    return form;
}

/**
 * Executa a ação de popular as opções de um campo
 * @param {Object} populateAction - Ação a ser executada
 * @param {string} fieldKey - Chave do campo
 * @returns {Promise<void>}
 */
async function executePopulateAction(populateAction, fieldKey) {
    if (populateAction.payload) {
        await store.dispatch(populateAction.name, populateAction.payload);
        return;
    }
    
    if (populateAction.withKey) {
        await store.dispatch(populateAction.name, { fieldKey });
        return;
    }
    
    await store.dispatch(populateAction.name);
}

function formMapper(data, formKey, proposalClientType) {
    //Limpar a cache - uso do require mantem a cache
    delete require.cache[require.resolve(`../forms/${formKey}.json`)];
    delete require.cache[require.resolve(`../forms/proposalTabs/tab-4-company.json`)];
    Array.from({length: 6}, (_, i) => i + 1).forEach(i => delete require.cache[require.resolve(`../forms/proposalTabs/tab-${i}.json`)]);
    const formObject = require(`../forms/${formKey}.json`);
    const isENIType = proposalClientType.clientTypeId === 2;


    const tabs = Array.from({length: 6}, (_, i) => i + 1).map(i => require(`../forms/proposalTabs/tab-${i}.json`));
    formObject.tabs = tabs;

    const participantOptions = [{
        value: -1,
        text: i18n.t('dropdownSelection'),
        disabled: true,
    }];
    data.lstParticipants.map((participant) => {
        participantOptions.push({
            value: participant?.position,
            text: i18n.t("pages.ProposalsPage.proposalsForm.bankingInfo.bankData.nibHolder."+nibHolderParticipantMap[participant?.position])
        })
    });

      store.commit("FormModule/setParticipantOptions", participantOptions);

      // Se for empresa troca o tipo de formulário para empresa - tab-4 ---> to ---> tab-4-company
      const formParticipantCompany = require(`../forms/proposalTabs/tab-4-company.json`);
      if (proposalClientType.clientTypeId === 3) formObject.tabs[3] = formParticipantCompany;

    const {idFinancial, lastIdState, financialContractCode, dtActivateContract, financialData, salesPoint, carData } = data.proposal;
    const idProduct = financialData?.idProduct;
    const financialNationality = financialNationalityMap.find((financialInfo) => financialInfo.financialIds.includes(idFinancial))?.nationality;
    // ES Financial IDs - 12, 13, 16
    // PT Financial IDs - 2...
    store.commit("FormModule/setFormNationality", financialNationality);
    store.commit("FormModule/setActiveBankInfo", {value: 1, nationality: financialNationality});
    store.commit("ProposalsModule/setProposalStatus", lastIdState);
    store.commit("ProposalsModule/setProposalFinId", idFinancial);
    store.commit("ProposalsModule/setProposalProdId", idProduct);

    store.commit("ProposalsModule/setProposalEntityId", salesPoint?.canvasserIdEntity);
    store.commit("ProposalsModule/setHasCompanyICDoc", data?.hasCompanyICDoc);
    store.commit("ProposalsModule/setProposalCarBrand", carData?.carBrand);

    store.commit("ProposalsModule/setShowBtnChangeStatus", data?.showBtnChangeStatus || false);

    console.log('showBtnVideoIdentification', data?.showBtnVideoIdentification);
    store.commit("ProposalsModule/setShowBtnVideoIdentification", data?.showBtnVideoIdentification || true);

    formObject.tabs[3].form = filterOutNotUsedFieldsByNationality(formObject.tabs[3].form, financialNationality);

    //Ensure that all values of object financialData have primitive type
    //Spread RewardClient object
    data.proposal.financialData = {
        ...data.proposal.financialData,
        ...data.proposal.financialData.rewardClient,
    };

    data.proposal.financialData.financialOrIncomeMonth1 = data.proposal.financialData.incrementalMonth1 === 0 ?
        data.proposal.financialData.financial
        : data.proposal.financialData.incrementalMonth1;

    data.lstParticipants = data.lstParticipants.map(participant => {
        const position = participant.position;
        return {
            ...participant,
            idClienttype: data.proposal.lstparticipantPosition.find(participantData => participantData.position === position)?.idClienttype
        };
    });

    if (data?.lstParticipants?.length === 0) {
        return false;
    }

    const lstParticipantsList = data.lstParticipants;

    handleFormTableToIncludeParticipants(data);
    addParticipantsTabs(data, lstParticipantsList, formObject, proposalClientType, financialNationality, idFinancial);

    const financialCode = data.proposal.identification.financialCode;
    const gSCCode = data.proposal.identification.GSCCode;
    const history = data?.history;
    const expensesClient = data.proposal.financialData.expensesClient;
    const capitalToFinance = data.proposal.financialData.capitalToFinance + expensesClient;
    const vin = data.proposal.carData.carChassis;
    const plate = data.proposal.carData.carPlate;
    const dtPlate = data.proposal.carData.carDtPlate;
    let canSign = false;
    if ((vin !== undefined && vin !== "") || (plate !== undefined && plate !== "") || (dtPlate !== undefined && dtPlate !== "")) {
        canSign = true;
    }
    store.commit("FormModule/setHistory", history);

    // https://dtogsc.atlassian.net/browse/SIFA-1468
    // Conclusão o campo é o mesmo para portes e comissão - logo, colocar:
    // Se Santander e Cetelem então aparece a dizer comissão de gestão.
    // If Santander or Cetelem
    // Alteração de label de um campo, consoante a financeira
    isItComissionOrPorts(formObject, idFinancial);

    try {
        accountAgeInputLoader(idFinancial, formObject);

        formObject.tabs.forEach((tab) => {
            const tabKey = tab.key;
            tab.form.forEach((form) => formMapperHandler(form, idFinancial, data, formKey, tabKey, financialNationality, proposalClientType, isENIType, tab));
        });

        formShaperBasedOnProposalStatus(formObject, lastIdState, capitalToFinance, financialCode, gSCCode, data, financialContractCode, dtActivateContract, idFinancial, history, canSign);
        expensesAndTaxFinancedValueUpdate(formObject);
        insuranceValueUpdate(formObject);
        relationProponentOnCompanyHandler(formObject, lastIdState, financialNationality);
        bbvaEsChassisRequirement(idFinancial, formObject);
    } catch (e) {
        console.log(e);
    }

    return formObject;
}

function bbvaEsChassisRequirement(idFinancial, formObject) {
    if (requiresChassis(idFinancial)) {
        const carTab = formObject.tabs.find((tab) => tab?.id === 2);
        const carChassisObject = carTab.form.map(formItem => formItem.fields.find(field => field.key === "carChassis")
        ).find(Boolean);
        carChassisObject.required = true;
    }
}

function accountAgeInputLoader(idFinancial, formObject) {
    if (Number(idFinancial) === FINANCIAL_IDS.CAIXABANK || Number(idFinancial) === FINANCIAL_IDS.CETELEM_ES 
        || Number(idFinancial) === FINANCIAL_IDS.SANTANDER_CONSUMER_ES
    ) {

        const infoBankTab = formObject?.tabs?.find((tab) => tab?.id === 5);

        infoBankTab.form[0].fields.push({
            "label": "pages.ProposalsPage.proposalsForm.bankingInfo.bankData.label.accountAgeDate",
            "type": "date",
            "key": "accountAgeDate",
            "saveKey": "accountAgeDate",
            "toastMaxDateMessage": "pages.ProposalsPage.proposalsForm.participantsData.personalData.label.minDateID",
            "typeDateValidation": "currentMaxAge",
            "value": "",
            "editable": true,
            "required": true,
            "flexdir": "col",
            "colspan": 3,
            "fieldShow": true,
            "proposalType": [
                "ES"
            ]
        });
    }
}

function relationProponentOnCompanyHandler(formObject, lastIdState, financialNationality) {
    if (financialNationality === "PT") {
        //Não foi possível fazer esta excepção dentro do formMapperHandler, porque essa função apenas permite alterar a secção do field
        formObject?.tabs?.forEach((tab) => tab?.form.map((form) => form?.fields?.map((field) => {
            if (field.key.startsWith("relationToProponent")) {
                const isACompanyFirstParticipant = store.getters["ProposalsModule/getIsCompanyClientType"];
                const firstParticipantType = store.getters["ProposalsModule/getProposalClientType"];
                const isAENIFirstParticipant = firstParticipantType?.clientTypeId === 2;
                // Dados Pessoais Detalhados
                // https://dtogsc.atlassian.net/browse/SIFA-1679
                // Repor o campo: Estado Cívil para o sócio
                // const personalInfoSection = tab?.form?.find((formX) => formX?.key?.startsWith("personalInfo"));
                // // Habitação
                // const residenceDataSection = tab?.form?.find((formX) => formX?.key?.startsWith("residenceData"));
                // Dados de rendimento
                const profitDataSection = tab?.form?.find((formX) => formX?.key?.startsWith("profit"));
                //Quando o primeiro titular é empresa e os restantes titulares são do tipo "Sócio"
                const preConditionToSectionShow = (isACompanyFirstParticipant || isAENIFirstParticipant) && (field?.value === "Sócio" || field?.value === "Beneficiário" || field?.value === "Representante");
                const quota = form.fields.find((f) => f?.key?.startsWith("quota"));

                const updateField = (field) => {
                    field.fieldShow = !preConditionToSectionShow && field?.proposalType?.includes("PT");
                    if (!field.fieldShow) {
                        field.value = "";
                    }
                    return field;
                };

                // personalInfoSection?.fields?.map(updateField);
                // residenceDataSection?.fields?.map(updateField);
                profitDataSection?.fields?.map(updateField);
                if (quota) {
                    quota.required = preConditionToSectionShow;
                    quota.editable = lastIdState !== 0 ? false : preConditionToSectionShow;
                    quota.value = preConditionToSectionShow ? quota.value : "";
                }
            }
        })));
    }
}

function insuranceValueUpdate(formObject) {
    // TAB 3 - Financial Data
    // 2º Secção
    const financialDataSecondSectionFields = formObject?.tabs?.find((tab) => tab.id === 3)?.form?.find((formFields) => formFields.sectionId === 2)?.fields;

    const insurancePPCName = financialDataSecondSectionFields?.find((field) => field?.key === 'insurancePPCName');
    insurancePPCName.value = insurancePPCName?.value ? insurancePPCName?.value : "Sem Seguro";
}

function expensesAndTaxFinancedValueUpdate(formObject) {
    //https://dtogsc.atlassian.net/browse/SIFA-1621
    //Se as despesas e impostos estão financiados, então deve aparecer na proposta nos dados financeiros a dizer: Financiado

    // TAB 3 - Financial Data
    // 1º Secção
    const financialDataFirstSectionFields = formObject?.tabs?.find((tab) => tab.id === 3)?.form?.find((formFields) => formFields.sectionId === 1)?.fields;
    // Despesas
    const expensesClient = financialDataFirstSectionFields?.find((field) => field?.key === 'expensesClient');
    const expensesFinance = financialDataFirstSectionFields?.find((field) => field?.key === 'expensesFinance');

    store.commit("ProposalsModule/setExpensesClient", expensesClient?.value);
    expensesClient.value = expensesFinance?.value === "S" ? "Financiado" : expensesClient?.value;
    expensesClient.inputIcon = expensesFinance?.value === "S" ? "" : "euro";

    // Imposto
    const taxStamp = financialDataFirstSectionFields?.find((field) => field?.key === 'taxStamp');
    const taxstampFinance = financialDataFirstSectionFields?.find((field) => field?.key === 'taxstampFinance');

    taxStamp.value = taxstampFinance?.value === "S" ? "Financiado" : taxStamp?.value;
    taxStamp.inputIcon = taxstampFinance?.value === "S" ? "" : "euro";
}

function isItComissionOrPorts(formObject, idFinancial) {
    formObject?.tabs[2]?.form[0]?.fields.map((field) => {
        if (field?.key === "portsExpenses") {
            field.label = idFinancial === FINANCIAL_IDS.SANTANDER_CONSUMER || idFinancial === FINANCIAL_IDS.CETELEM ? "pages.ProposalsPage.proposalsForm.financialData.financialInfo.label.managmentComission" : "pages.ProposalsPage.proposalsForm.financialData.financialInfo.label.portsExpenses";
        }
    });
}

function modifyKeyValues(obj, keySuffix, shouldAddSuffix) {
    for (let prop in obj) {
        if (typeof obj[prop] === 'object' && obj[prop] !== null) {
            modifyKeyValues(obj[prop], keySuffix, shouldAddSuffix);
        } else if (prop === 'key') {
            if (shouldAddSuffix) {
                obj[prop] = obj[prop] + "_" + keySuffix;
            } else {
                obj[prop] = obj[prop].replace(new RegExp("_" + keySuffix + "$"), "");
            }
        }
    }
    return obj;
}

/** recebe o objeto do formulário guardado e preenche de acordo com o formData saveKeys */
const formUnmapper = ({saveObject, formData}, formNationality, clientType) => {
    delete require.cache[require.resolve("../forms/proposalTabs/participantTabs/company.json")];
    delete require.cache[require.resolve("../forms/proposalTabs/participantTabs/particular.json")];
    const particularExtraClientPerson = require("../forms/proposalTabs/participantTabs/particular.json");
    const companyExtraClientPerson = require("../forms/proposalTabs/participantTabs/company.json");
    const isItACompanyForm = clientType.clientTypeId === 3;
    const isENIType = clientType.clientTypeId === 2;
    const firstClientType = isItACompanyForm ? "firstClientCompany" : "firstClientPerson";
    const unmappedFormObj = saveObject;
    // Prevenir que altere o objeto do formulário original
    // Cria uma cópia do objeto dos tabs do formulário
    const tabs = JSON.parse(JSON.stringify(formData.tabs));

    // Alterar o tipo de objeto do primeiro participante, consoante o tipo de participante - Empresa ou Particular
    unmappedFormObj.clientInfo = {
        [firstClientType]: isItACompanyForm ? {...companyExtraClientPerson} : {...particularExtraClientPerson},
        "firstClientType": clientType.clientTypeId
    }

    tabs.map((formTab) => {
        // se o tab tiver um saveKey, podemos salvar
        if (formTab.saveKey) {
            if (formTab.dynamicallyCreated) {
                delete require.cache[require.resolve("../forms/proposalTabs/participantTabs/company.json")];
                delete require.cache[require.resolve("../forms/proposalTabs/participantTabs/particular.json")];
                const particularExtraClientPerson = require("../forms/proposalTabs/participantTabs/particular.json");

                const clientInterventionType = {
                    //Particular
                    "secondClientPerson": "secondInterventionType",
                    "thirdClientPerson": "thirdInterventionType",
                    "fourthClientPerson": "fourthInterventionType",
                    //Company
                    "secondClientCompany": "secondCnyInterventionType",
                    "thirdClientCompany": "thirdCnyInterventionType",
                    "fourthClientCompany": "fourthCnyInterventionType"
                };

                //TODO: FIX
                let interventionType = "";
                let clientTypeId = 1;
                formTab.form.forEach((form) => form.fields.find((field) => {
                    if (field?.key?.startsWith("interventionType") && field.value) {
                        interventionType = field.value
                    }
                    if (field?.key?.startsWith("idClienttype")) {
                        clientTypeId = field.value;
                    }
                }))

                console.log(isENIType);

                const clienTypeMapping = {
                    //Particular
                    "secondClientPerson": {type: "secondClientType", keyPreffix: "1", interventionType: clientTypeId},
                    "thirdClientPerson": {type: "thirdClientType", keyPreffix: "2", interventionType: clientTypeId},
                    "fourthClientPerson": {type: "fourthClientType", keyPreffix: "3", interventionType: clientTypeId},
                    //Company
                    "secondClientCompany": {type: "secondClientType", keyPreffix: "1", interventionType: 3},
                    "thirdClientCompany": {type: "thirdClientType", keyPreffix: "2", interventionType: 3},
                    "fourthClientCompany": {type: "fourthClientType", keyPreffix: "3", interventionType: 3}
                };

                // unmappedFormObj.clientInfo[formTab.saveKey] = isItACompanyForm ? { ...companyExtraClientPerson } : { ...particularExtraClientPerson };
                unmappedFormObj.clientInfo[formTab.saveKey] = {...particularExtraClientPerson};
                // unmappedFormObj.clientInfo[clienTypeMapping[formTab?.saveKey]?.type] = clientType.clientTypeId;
                unmappedFormObj.clientInfo[clienTypeMapping[formTab?.saveKey]?.type] = clienTypeMapping[formTab?.saveKey]?.interventionType;
                unmappedFormObj.clientInfo[clientInterventionType[formTab?.saveKey]] = interventionType;

                formTab.form = modifyKeyValues(formTab.form, clienTypeMapping[formTab?.saveKey]?.keyPreffix, false)
            }
            formTab.form.map((form) => {
                //console.log('form: ', form)
                form.fields.map((field) => {
                    // if field has saveKey we check where it is supposed to sav
                    if (field.saveKey) {
                        if (!field.value) {
                            
                            return;
                        }
                        if (formTab.parentSaveKey && !form.saveKey) {
                            if (field?.key?.startsWith("gender")) {
                                let genderToSave = "";
                                genderToSave = field.value === 1 || field.value === "1" ? "M" : "F";
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey][
                                    field.saveKey
                                    ] = genderToSave;
                            } else if (field?.key?.startsWith("beneficialOwner")) {
                                let beneficialOwnerToSave = "";
                                beneficialOwnerToSave = field.value === 1 || field.value === "1" ? "S" : "N";
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey][
                                    field.saveKey
                                    ] = beneficialOwnerToSave;
                            } else if (form?.key?.startsWith("rgpd")) {
                                let rgpdToSave = "";
                                rgpdToSave = field.value === 1 || field.value === "1" ? "S" : "N";
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey][
                                    field.saveKey
                                    ] = rgpdToSave;
                            } else if (form?.key?.startsWith("oficialDataToggle")) {
                                let toggleFieldsToSave = "";
                                toggleFieldsToSave = field.value === 1 || field.value === "1" ? "S" : "N";
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey][
                                    field.saveKey
                                    ] = toggleFieldsToSave;
                            } else if (field?.key?.startsWith("countryActivities")) {
                                if (field.value.length !== 0) {
                                    unmappedFormObj[formTab.parentSaveKey][formTab.saveKey][
                                        field.saveKey
                                        ] = field.value;
                                }
                            } else {
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey][
                                    field.saveKey
                                    ] = field.value;
                            }
                        } else if (formTab.parentSaveKey && form.saveKey) {
                            if (form.saveKey === "taxAddress") {
                                if (field.key.startsWith("address1SameAddress2")) {
                                    unmappedFormObj[formTab.parentSaveKey][formTab.saveKey]["address"][field.saveKey] = field.value === 1 || field.value === "1" ? "S" : "N";
                                } else {
                                    unmappedFormObj[formTab.parentSaveKey][formTab.saveKey]["address"][field.saveKey] = field.value;
                                }
                            } else if (form.saveKey === "politicalPosition") {
                                if (field?.type === "neo-toggle") {
                                    unmappedFormObj[formTab.parentSaveKey][formTab.saveKey]["politicalPosition"][field.saveKey] = field.value === 1 || field.value === "1" ? "S" : "N";
                                } else {
                                    unmappedFormObj[formTab.parentSaveKey][formTab.saveKey]["politicalPosition"][field.saveKey] = field.value;
                                }
                            } else if (field?.key?.startsWith("isSickLeave")) {
                                let isSickLeaveToSave = "";
                                isSickLeaveToSave = field.value === 1 || field.value === "1" ? "S" : "N";
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey]["profit"][field.saveKey] = isSickLeaveToSave;
                                const netReceiptValueAvg = form.fields.find((f) => f.key.startsWith("netReceiptValueAvg"));

                                if (isSickLeaveToSave === "S") {
                                    unmappedFormObj[formTab.parentSaveKey][formTab.saveKey]["profit"]["sickLeaveValueReceiptValue"] = Number(netReceiptValueAvg?.value);
                                }
                            } else if (field?.key?.startsWith("hasFullProofIncome")) {
                                let hasFullProofIncomeToSave = "";
                                hasFullProofIncomeToSave = field.value === 1 || field.value === "1" ? "S" : "N";
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey]["profit"][field.saveKey] = hasFullProofIncomeToSave;
                            } else {
                                unmappedFormObj[formTab.parentSaveKey][formTab.saveKey][form.saveKey][field.saveKey] =
                                    field.value;
                            }
                        } else {
                            if (field.key === "NIB") {
                                let nibToSave = "";
                                const isNational = form.fields.find((f) => f.key === "isNational");
                                const prefix = formNationality === "ES" ? "ES" : "PT50";
                                nibToSave = isNational.value === "Internacional" ? field.value : prefix + field.value;
                                unmappedFormObj[formTab.saveKey][field.saveKey] = nibToSave;
                            } else if (field.key === "nibHolder") {
                                const valueToFind = field?.value || 'Primeiro Interveniente';
                                const nrParticipation = field?.options?.find((option) => option.text == valueToFind)?.value || 1;
                                unmappedFormObj[formTab.saveKey][field.saveKey] = nrParticipation;
                            } else if (field.key === "carDtplate") {
                                let carDtplate = field.value; // example date string
                                if (carDtplate.endsWith("Z")) {
                                    carDtplate = carDtplate.slice(0, -1); // remove the last character ("Z")
                                }
                                unmappedFormObj[formTab.saveKey][field.saveKey] = carDtplate;
                            } else if (field?.key?.startsWith("carService")) {
                                let carServiceToSave = "";
                                carServiceToSave = (field.value === 1 || field.value === "1") ? "S" : "N";
                                unmappedFormObj[formTab.saveKey][field.saveKey] = carServiceToSave;
                            } else if (field?.key?.startsWith("salesman")) {
                                const findSalesman = field.options.find((option) => option.value == field.value);
                                unmappedFormObj[formTab.saveKey]["salespointSalesmanEmail"] = field.options.find((option) => option.selected)?.businessManagerEmail || '';
                                unmappedFormObj[formTab.saveKey][field.saveKey] = findSalesman ? field.value : '';
                            } else {
                                unmappedFormObj[formTab.saveKey][field.saveKey] = field.value;
                            }

                        }
                    }
                });
            });
        }
    });

    return unmappedFormObj;
};

export {formMapper, formUnmapper, formMapperHandler, filterOutNotUsedFieldsByNationality, nibHolderParticipantMap};


